/**
 * Campaigns Service Index
 *
 * Main API service for campaigns module.
 * Provides direct API calls (not React Query hooks).
 */

import {
    ALOGroup,
    BrandAsset,
    Campaign,
    CampaignALO,
    CampaignTarget,
    LibraryALOGroup,
    LibraryCompany,
    LibraryGoal,
    LibraryPerson,
    LibraryProduct,
    LibraryGroup,
    LibraryStory,
    ScrapedWebsite,
    StoryBeat,
} from '@/types/campaigns'
import { parseALOsByTypePayload, parseAddALOsResponse } from '@/modules/persuasion-lab/utils/parse-alos-by-type'
import { NODE_BASE_URL, NodeAxios } from '../api'
import { API_TIMEOUTS } from '@/config/api-timeouts'
export { campaignAssetService } from './asset-service'
export { contentStrategyService } from './content-strategy.service'
export type {
    ContentIdeaData,
    ContentStrategyPackData,
    ContentStrategyPackStatus,
    IdeaStatus,
    MethodFit,
    MethodStrategyData,
    RouteIdeaPayload,
} from './content-strategy.service'
export { interviewBotService } from './interview-service'
export { messageStrategyService } from './message-strategy.service'
export { scenarioService } from './scenario-service'
export { identityEngineService } from './identity-engine-api'
export type { LoadScenarioResult, Scenario, ScenarioFile, ScenarioFileManifest } from './scenario-service'
export { valuePropsService } from './value-props.service'

// ============================================
// Website Scraping for Target Research
// ============================================

interface WebsiteAnalysisResult {
    screenshotUrl?: string
    content?: {
        title?: string
        metaDescription?: string
        metaKeywords?: string
        headings?: {
            h1?: string
            h2?: string
            h3?: string
            h4?: string
            h5?: string
            h6?: string
        }
        paragraphs?: string
        links?: string
        images?: string
        lists?: string
        buttons?: string
        forms?: string
        mainContent?: string
        bodyText?: string
    }
    // API wraps response in data field
    data?: {
        screenshotUrl?: string
        content?: {
            title?: string
            metaDescription?: string
            metaKeywords?: string
            headings?: {
                h1?: string
                h2?: string
                h3?: string
                h4?: string
                h5?: string
                h6?: string
            }
            paragraphs?: string
            links?: string
            images?: string
            lists?: string
            buttons?: string
            forms?: string
            mainContent?: string
            bodyText?: string
        }
    }
}
void (null as any as WebsiteAnalysisResult)

interface DiscoveredTarget {
    name: string
    role: string
    description: string
    relevance_score: number
    sub_targets: Array<{
        name: string
        description: string
    }>
}
void (null as any as DiscoveredTarget)

export interface TargetDiscoveryResult {
    targets: DiscoveredTarget[]
    source_summary: string
    method: 'describe' | 'website' | 'competitor'
    analyzed_url?: string
}

export interface EnrichedTarget {
    name: string
    role: string
    industry: string
    company_size: string
    description: string
    problems: string[]
    needs: string[]
    priorities: string[]
    // Strategic fields
    goals: string[]
    solutions: string[]
    objections: string[]
    values: string[]
    aspirations: string[]
    // Authority & power
    budget_authority: string
    decision_making_power: string
    // Behavioral
    behaviors: string[]
    influences: string[]
    is_primary: boolean

    // Identity Theory Fields (Strategic DNA)
    identity_states: {
        failure: {
            label: string
            indicators: string[]
        }
        transition: {
            label: string
            indicators: string[]
        }
        success: {
            label: string
            indicators: string[]
        }
    }
    decision_risk_framework: {
        high_risk: string
        moderate_risk: string
        low_risk: string
    }

    // Sub-target hierarchy
    is_sub_target?: boolean
    parent_target_snapshot?: {
        name: string
        role: string
        description?: string
        relevance_score?: number
        industry?: string
        company_size?: string
        // All sub-targets discovered (including non-selected ones for negative targeting)
        all_sub_targets?: Array<{
            name: string
            description: string
            was_selected: boolean
        }>
    }
}

export interface TargetEnrichmentResult {
    enriched_targets: EnrichedTarget[]
}

export interface GeneratedGoal {
    name: string
    description: string
    goal_type: 'product' | 'company' | 'people'
    metrics: string[]
    messaging_themes: string[]
}

export interface CompanyMetadata {
    name: string
    industry?: string
    description?: string
    products?: string[]
}

export interface GoalGenerationResult {
    goals: GeneratedGoal[]
    company_metadata: CompanyMetadata
    source_url: string
}

export const websiteAnalysisService = {
    /**
     * Analyze a website URL for target audience research
     */
    async analyzeWebsite(url: string): Promise<WebsiteAnalysisResult> {
        const result = await NodeAxios.post('/website-screenshot', { url })
        return result as any
    },

    /**
     * Discover target audiences using AI
     */
    async discoverTargets(params: {
        campaignId?: string
        runId?: string
        method: 'describe' | 'website' | 'competitor'
        content: string
        url?: string
        additional_context?: string
        focus?: 'marketing' | 'visual'
    }): Promise<TargetDiscoveryResult> {
        // Increase timeout for AI target discovery - can take 30-60 seconds
        const result = await NodeAxios.post(
            params.campaignId
                ? `/campaigns/${params.campaignId}/targets/discover`
                : params.runId
                  ? `/visual-engine/runs/${params.runId}/targets/discover`
                  : '/campaigns/targets/discover',
            params,
            {
                timeout: API_TIMEOUTS.AI_GENERATION,
            }
        )
        return result.data as TargetDiscoveryResult
    },

    /**
     * Enrich selected targets with detailed profiles
     */
    async enrichTargets(params: {
        campaignId?: string
        targets: Array<{ name: string; role: string; description: string; relevance_score: number }>
        source_content?: string
        method: string
    }): Promise<TargetEnrichmentResult> {
        // Increase timeout for AI processing - can take 10-20 seconds per target
        const result = await NodeAxios.post(
            params.campaignId ? `/campaigns/${params.campaignId}/targets/enrich` : '/campaigns/targets/enrich',
            params,
            {
                timeout: API_TIMEOUTS.AI_GENERATION,
            }
        )
        return result.data as TargetEnrichmentResult
    },

    /**
     * Queue targets for async enrichment (RabbitMQ-based)
     * Returns immediately, targets are enriched in background
     */
    async queueTargetEnrichment(params: {
        campaignId?: string
        runId?: string
        targets: Array<{
            name: string
            role: string
            description: string
            relevance_score: number
            industry?: string
            company_size?: string
            is_sub_target?: boolean
            parent_target_snapshot?: any
        }>
        source_content?: string
        method: 'describe' | 'website' | 'competitor'
        research_context?: {
            source_url?: string
            additional_context?: string
        }
    }): Promise<{
        success: boolean
        jobIds: string[]
        targetCount: number
        estimatedTimePerTarget: number
    }> {
        const endpoint = params.campaignId
            ? `/campaigns/${params.campaignId}/targets/enrich-queue`
            : `/visual-engine/runs/${params.runId}/targets/enrich-queue`

        const result = await NodeAxios.post<
            any,
            {
                success: boolean
                jobIds: string[]
                targetCount: number
                estimatedTimePerTarget: number
            }
        >(endpoint, {
            targets: params.targets,
            source_content: params.source_content,
            method: params.method,
            research_context: params.research_context,
        })
        return result
    },

    /**
     * Queue library targets for async enrichment (RabbitMQ-based, no campaignId/runId)
     * Returns immediately, targets are enriched in background and saved to the Library.
     */
    async queueLibraryTargetEnrichment(params: {
        targets: Array<{
            name: string
            role: string
            description: string
            relevance_score: number
            industry?: string
            company_size?: string
        }>
        source_content?: string
        method: string
        research_context?: {
            source_url?: string
            additional_context?: string
        }
    }): Promise<{ success: boolean; jobIds: string[]; targetCount: number }> {
        const result = await NodeAxios.post<any, { success: boolean; jobIds: string[]; targetCount: number }>(
            '/campaigns/library/targets/enrich-queue',
            {
                targets: params.targets,
                source_content: params.source_content,
                method: params.method,
                research_context: params.research_context,
            }
        )
        return result
    },

    /**
     * Persist the raw discovery list to a campaign so the modal can offer "Resume"
     */
    async saveTargetResearchSession(
        campaignId: string,
        session: { targets: any[]; method: string; inputs?: Record<string, any> }
    ): Promise<void> {
        await NodeAxios.post(`/campaigns/${campaignId}/target-research-session`, session)
    },

    /**
     * Clear a persisted target research session
     */
    async clearTargetResearchSession(campaignId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/${campaignId}/target-research-session`)
    },

    /**
     * Save researched targets to a campaign
     */
    async saveResearchedTargets(params: {
        campaignId: string
        targets: Array<{
            name: string
            role: string
            industry?: string
            company_size?: string
            description?: string
            problems?: string[]
            needs?: string[]
            priorities?: string[]
            // Strategic fields
            goals?: string[]
            solutions?: string[]
            objections?: string[]
            values?: string[]
            aspirations?: string[]
            // Authority & power
            budget_authority?: string
            decision_making_power?: string
            // Behavioral
            behaviors?: string[]
            influences?: string[]
            is_primary?: boolean
            relevance_score?: number

            // Identity Theory Fields
            identity_states?: {
                failure: { label: string; indicators: string[] }
                transition: { label: string; indicators: string[] }
                success: { label: string; indicators: string[] }
            }
            decision_risk_framework?: {
                high_risk: string
                moderate_risk: string
                low_risk: string
            }

            // Sub-target hierarchy
            is_sub_target?: boolean
            parent_target_id?: string
            parent_target_snapshot?: {
                name: string
                role: string
                description?: string
                relevance_score?: number
                industry?: string
                company_size?: string
                // All sub-targets discovered (including non-selected ones for negative targeting)
                all_sub_targets?: Array<{
                    name: string
                    description: string
                    was_selected: boolean
                }>
            }
        }>
        research_context: {
            method: 'describe' | 'website' | 'competitor'
            source_url?: string
            source_content_preview?: string
            additional_context?: string
        }
        save_to_library?: boolean
    }): Promise<{ targets: any[]; library_targets?: any[]; count: number }> {
        const result = await NodeAxios.post(`/campaigns/${params.campaignId}/targets/batch-create`, {
            targets: params.targets,
            research_context: params.research_context,
            save_to_library: params.save_to_library,
        })
        return result.data as { targets: any[]; library_targets?: any[]; count: number }
    },
}

// ============================================
// ALO Generation Service
// ============================================

export const aloGenerationService = {
    /**
     * Generate ALOs from content using AI
     */
    async generateALOs(params: {
        content: string
        sourceUrl?: string
        additionalContext?: string
        groupLevel: 'company' | 'product' | 'person'
        groupName: string
    }): Promise<{
        alos: Array<{
            type: 'asset' | 'liability' | 'omission'
            category: string
            text: string
            title?: string
            description: string
            proof_points?: string[]
            proofUrl?: string
            proofStrength?: 'strong' | 'medium' | 'weak'
            persuasion_type?: string
            rhetoric_devices?: string[]
            related_products?: string[]
            confidence?: number
            isStrategic: boolean
            priority: number
        }>
        metadata: {
            sourceUrl?: string
            analyzedAt: string
            totalCount: number
            byType: {
                assets: number
                liabilities: number
                omissions: number
            }
        }
    }> {
        try {
            // Increase timeout for AI ALO generation - can take 30-60 seconds
            const result = await NodeAxios.post('/campaigns/alos/generate', params, {
                timeout: API_TIMEOUTS.AI_GENERATION,
            })

            // Handle response structure - backend returns { success: true, data: { alos, metadata } }
            const responseData = result.data?.data || result.data

            if (!responseData || !responseData.alos) {
                console.error('Invalid response structure:', result.data)
                throw new Error('Invalid response from ALO generation API')
            }

            return responseData
        } catch (error) {
            console.error('aloGenerationService.generateALOs: Error:', error)
            throw error
        }
    },

    /**
     * Chat with AI for ALO discovery (streaming)
     */
    async *chatALODiscovery(params: {
        messages: Array<{ role: 'user' | 'assistant'; content: string }>
        groupLevel: 'company' | 'product' | 'people'
        groupName: string
        contextInfo?: {
            productName?: string
            personName?: string
            companyName?: string
        }
    }): AsyncGenerator<string, void, unknown> {
        const response = await fetch('/api/campaigns/alos/chat', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(params),
        })

        if (!response.ok) {
            throw new Error(`Chat request failed: ${response.statusText}`)
        }

        if (!response.body) {
            throw new Error('No response body')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder()

        try {
            while (true) {
                const { done, value } = await reader.read()

                if (done) break

                const chunk = decoder.decode(value, { stream: true })
                const lines = chunk.split('\n')

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = JSON.parse(line.slice(6))

                        if (data.error) {
                            throw new Error(data.error)
                        }

                        if (data.done) {
                            return
                        }

                        if (data.content) {
                            yield data.content
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock()
        }
    },

    /**
     * Extract structured ALOs from conversation
     */
    async extractALOsFromChat(params: {
        conversation: Array<{ role: 'user' | 'assistant'; content: string }>
        groupLevel: 'company' | 'product' | 'people'
        groupName: string
    }): Promise<{
        alos: Array<{
            type: 'asset' | 'liability' | 'omission'
            text: string
            title?: string
            description: string
            category: string
            priority: number
            isStrategic: boolean
            proof_points?: string[]
            proofUrl?: string
            proofStrength?: 'strong' | 'medium' | 'weak'
            persuasion_type?: string
            rhetoric_devices?: string[]
            related_products?: string[]
            confidence?: number
        }>
    }> {
        // Increase timeout for AI ALO extraction - can take 30-60 seconds
        const result = await NodeAxios.post('/campaigns/alos/extract', params, {
            timeout: 120000, // 2 minutes for AI analysis
        })

        // NodeAxios returns the data directly, not wrapped in .data
        // Try both structures to be safe
        const data = result.data || result

        return data || { alos: [] }
    },
}

// ============================================
// Library ALO Groups Service
// ============================================

export const libraryALOGroupService = {
    /**
     * Save ALO group to library
     */
    async saveToLibrary(params: {
        level: 'company' | 'product' | 'people'
        name: string
        description?: string
        tags?: string[]
        assignedToType?: 'company' | 'product' | 'person'
        assignedToId?: string
        assignedToName?: string
        alos: Array<{
            type: 'asset' | 'liability' | 'omission'
            category: string
            text: string
            title?: string
            description?: string
            proof_points?: string[]
            proofUrl?: string
            proofStrength?: 'strong' | 'medium' | 'weak'
            persuasion_type?: string
            rhetoric_devices?: string[]
            related_products?: string[]
            confidence?: number
            isStrategic?: boolean
            priority?: number
        }>
    }): Promise<any> {
        const result = await NodeAxios.post('/campaigns/library/alo-groups', params)
        return result
    },

    /**
     * List library ALO groups
     */
    async listLibraryGroups(params?: {
        level?: 'company' | 'product' | 'people'
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
    }): Promise<{ groups: any[]; total: number }> {
        const queryParams = new URLSearchParams()
        if (params?.level) queryParams.append('level', params.level)
        if (params?.sortBy) queryParams.append('sortBy', params.sortBy)
        if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder)

        const result = await NodeAxios.get(`/campaigns/library/alo-groups?${queryParams}`)

        // NodeAxios returns data directly, not wrapped
        const data = result.data || result

        return data || { groups: [], total: 0 }
    },

    /**
     * Get specific library ALO group
     */
    async getLibraryGroup(groupId: string): Promise<any> {
        const result = await NodeAxios.get(`/campaigns/library/alo-groups/${groupId}`)
        return result
    },
}

// ============================================
// Campaigns API
// ============================================

export const campaignsService = {
    /**
     * List all campaigns
     */
    async listCampaigns(filters?: {
        status?: string
        campaign_type?: string
        functional_type?: string
        owner?: string
    }): Promise<Campaign[]> {
        const params = new URLSearchParams()
        if (filters?.status) params.append('status', filters.status)
        if (filters?.campaign_type) params.append('campaign_type', filters.campaign_type)
        if (filters?.functional_type) params.append('functional_type', filters.functional_type)
        if (filters?.owner) params.append('owner', filters.owner)

        const queryString = params.toString()
        const result = await NodeAxios.get(`/campaigns${queryString ? `?${queryString}` : ''}`)
        return result as any
    },

    /**
     * Get campaign by ID
     */
    async getCampaign(id: string): Promise<Campaign> {
        return await NodeAxios.get(`/campaigns/${id}`)
    },

    /**
     * Get effective image settings (Visual Identity + campaign overrides resolved)
     * GET /api/campaigns/:id/effective-image-settings
     */
    async getEffectiveImageSettings(campaignId: string): Promise<{
        visualContext: any
        visualRunId: string | null
        campaignOverridesApplied: boolean
        conflictsWithLocks?: string[]
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/effective-image-settings`)
        const body = (res as any).data
        return body?.data ?? body ?? res
    },

    /**
     * Get campaign topics for import
     * GET /api/campaigns/:id/topics
     */
    async getCampaignTopics(id: string): Promise<{ campaignType: string; topics: any[] }> {
        const result = await NodeAxios.get(`/campaigns/${id}/topics`)
        return result as any
    },

    /**
     * List narrative-ready Persuasion Lab drafts for "Pull from Persuasion Lab" UI.
     * GET /api/campaigns/:campaignId/topics/persuasion-lab-drafts
     */
    async listPersuasionLabDrafts(
        campaignId: string,
        options?: { run_id?: string }
    ): Promise<{
        drafts: Array<{
            _id: string
            display_title: string
            narrative_topic_title: string
            narrative_channel_type: string
            narrative_notes?: string
            run_id?: string
            updated_at: string
        }>
    }> {
        const params = options?.run_id ? `?run_id=${encodeURIComponent(options.run_id)}` : ''
        return (await NodeAxios.get(`/campaigns/${campaignId}/topics/persuasion-lab-drafts${params}`)) as any
    },

    /**
     * List ALL DraftArtifacts and Documents from the campaign's linked Persuasion Lab run(s),
     * without requiring narrative tagging. Used for "Browse by Outcome" in the Pull from Lab panel.
     * GET /api/campaigns/:campaignId/topics/run-drafts
     */
    async listRunDrafts(campaignId: string): Promise<{
        success: boolean
        hasLinkedRun: boolean
        runIds?: string[]
        drafts: Array<{
            _id: string
            type: 'draft' | 'document'
            display_title: string
            narrative_topic_title: string | null
            narrative_channel_type: string | null
            narrative_notes: string | null
            situation_id: string | null
            copy_bank_id: string | null
            argument_id: string | null
            outcome_id: string | null
            run_id: string | null
            updated_at: string
        }>
        documents: Array<{
            _id: string
            type: 'draft' | 'document'
            display_title: string
            narrative_topic_title: string | null
            narrative_channel_type: string | null
            situation_id: string | null
            copy_bank_id: string | null
            run_id: string | null
            updated_at: string
        }>
    }> {
        return (await NodeAxios.get(`/campaigns/${campaignId}/topics/run-drafts`)) as any
    },

    /**
     * Import Persuasion Lab draft artifacts as topics on beats.
     * POST /api/campaigns/:campaignId/topics/from-persuasion-lab
     * Uses suggested beats per draft if beat_assignment is omitted.
     */
    async importTopicsFromPersuasionLab(
        campaignId: string,
        body: { draft_artifact_ids: string[]; beat_assignment?: Record<string, number[]> }
    ): Promise<{
        placed: Array<{ draft_artifact_id: string; beat_number: number; topic_title: string }>
        skipped: Array<{ draft_artifact_id: string; reason: string }>
    }> {
        const res = (await NodeAxios.post(`/campaigns/${campaignId}/topics/from-persuasion-lab`, body)) as any
        return { placed: res?.placed ?? [], skipped: res?.skipped ?? [] }
    },

    /**
     * Retroactively attach situation-matched drafts/documents to existing topics for a beat.
     * POST /api/campaigns/:campaignId/beats/:beatId/situations-attach
     */
    async retroactivelyAttachSituationDrafts(
        campaignId: string,
        beatId: string
    ): Promise<{ success: boolean; message: string }> {
        return (await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/situations-attach`, {})) as any
    },

    /**
     * Create new campaign
     */
    async createCampaign(data: Partial<Campaign>): Promise<Campaign> {
        try {
            const result = await NodeAxios.post<Campaign>('/campaigns', data)
            return result as any
        } catch (error) {
            console.error('campaignsService.createCampaign: API error:', error)
            throw error
        }
    },

    /**
     * Update campaign
     */
    async updateCampaign(id: string, data: Partial<Campaign>): Promise<Campaign> {
        return await NodeAxios.patch(`/campaigns/${id}`, data)
    },

    /**
     * Delete campaign (soft delete)
     */
    async deleteCampaign(id: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/campaigns/${id}`)
    },

    // ============================================
    // Communication Brief (Messaging Campaigns)
    // ============================================

    /**
     * ATOMIC ACTION: Create Messaging Campaign WITH Communication Brief
     * POST /campaigns/create-messaging-campaign-with-brief
     */
    async createMessagingCampaignWithBrief(data: {
        foundation_run_id: string
        primary_claim_id: string
        primary_value_prop_id: string
        profile_type: 'product' | 'company' | 'person'
        profile_id: string
    }): Promise<{ campaign: Campaign; brief: any }> {
        return await NodeAxios.post('/campaigns/create-messaging-campaign-with-brief', data)
    },

    /**
     * Generate Communication Brief for existing campaign
     * POST /campaigns/:campaignId/communication-brief/generate
     */
    async generateCommunicationBrief(
        campaignId: string,
        data: {
            primary_claim_id: string
            primary_value_prop_id: string
            foundation_run_id: string
        }
    ): Promise<{ brief: any }> {
        return await NodeAxios.post(`/campaigns/${campaignId}/communication-brief/generate`, data)
    },

    /**
     * Get Communication Brief for campaign
     * GET /campaigns/:campaignId/communication-brief
     */
    async getCommunicationBrief(campaignId: string): Promise<{ brief: any }> {
        return await NodeAxios.get(`/campaigns/${campaignId}/communication-brief`)
    },

    /**
     * Update Communication Brief (manual edits)
     * PATCH /campaigns/:campaignId/communication-brief
     */
    async updateCommunicationBrief(campaignId: string, updates: Partial<any>): Promise<{ brief: any }> {
        return await NodeAxios.patch(`/campaigns/${campaignId}/communication-brief`, updates)
    },

    /**
     * Seed campaign strategy (goals, target, TFD, ALO) from linked Persuasion Lab foundation run (one-time).
     * POST /campaigns/:id/seed-from-foundation
     */
    async seedFromFoundation(
        campaignId: string
    ): Promise<{ goalsSeeded: number; targetsSeeded: number; alosSeeded: number }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/seed-from-foundation`)
        return (res as any)?.data ?? res
    },

    /**
     * Get recommended persuasion frameworks for landing page
     * GET /campaigns/:id/landing-page/recommendation
     */
    async getLandingPageRecommendation(
        campaignId: string
    ): Promise<{ primary: string[]; supporting: string[]; rationale: string }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/recommendation`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate copywriting brief for landing page from campaign context
     * POST /campaigns/:id/landing-page/generate-copywriting-brief
     */
    async generateLandingPageCopywritingBrief(
        campaignId: string,
        _options?: { forceRegenerate?: boolean }
    ): Promise<{ copywritingBrief: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/generate-copywriting-brief`, {})
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get recommended blocks for landing page (ranked by persuasion framework)
     * GET /campaigns/:id/landing-page/recommended-blocks?primary=ethos&supporting=logos,pathos
     */
    async getLandingPageRecommendedBlocks(
        campaignId: string,
        params?: { primary?: string; supporting?: string }
    ): Promise<{ blocks: Array<{ id: string; name: string; description: string; tfd: string; score: number }> }> {
        const search = new URLSearchParams()
        if (params?.primary) search.set('primary', params.primary)
        if (params?.supporting) search.set('supporting', params.supporting)
        const qs = search.toString()
        const url = qs
            ? `/campaigns/${campaignId}/landing-page/recommended-blocks?${qs}`
            : `/campaigns/${campaignId}/landing-page/recommended-blocks`
        const res = await NodeAxios.get(url)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get block taxonomy for Section Builder
     * GET /campaigns/:id/landing-page/blocks
     */
    async getLandingPageBlocks(campaignId: string): Promise<{
        blocks: Array<{ id: string; name: string; description: string; tfd: string }>
        variants: Record<string, string[]>
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/blocks`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get landing page patterns (goal-driven) and default for current campaign goal.
     * GET /campaigns/:id/landing-page/patterns
     */
    async getLandingPagePatterns(campaignId: string): Promise<{
        patterns: Array<{
            id: string
            label: string
            description: string
            useWhen: string
            identityArc: string
            sections: Array<{ block_type: string; tfd_goal: string }>
        }>
        defaultPatternId: string
        currentPatternId: string | null
        currentPatternRevision: string | null
        patternRevisions: Record<string, string>
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/patterns`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Apply a pattern (replace sections with pattern). Start from Pattern mode.
     * POST /campaigns/:id/landing-page/apply-pattern
     */
    async applyLandingPagePattern(
        campaignId: string,
        patternId: string
    ): Promise<{
        sections: Array<{ id: string; block_type: string; order: number; tfd_goal: string }>
        pattern_id: string
        pattern_revision: string
        landing_page_config: Record<string, unknown>
    }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/apply-pattern`, { patternId })
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Apply draft (blueprint + copy + visual prompts) from the campaign's linked Persuasion Lab message run.
     * POST /campaigns/:id/landing-page/import-blueprint-from-message-run
     * mode: 'overwrite' (default) replaces sections; 'append' adds draft sections after current ones.
     */
    async importBlueprintFromMessageRun(
        campaignId: string,
        options?: { mode?: 'overwrite' | 'append' }
    ): Promise<{
        sections: Array<{ id: string; block_type: string; order: number; tfd_goal: string }>
        landing_page_config: Record<string, unknown>
    }> {
        const res = await NodeAxios.post(
            `/campaigns/${campaignId}/landing-page/import-blueprint-from-message-run`,
            options?.mode ? { mode: options.mode } : {}
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Import landing page sections and copy from a Persuasion Lab rough draft document.
     * GET /campaigns/:id/landing-page/draft-import-audit?documentId=xxx
     * Returns how the draft would be parsed and mapped (segments, unmapped_segment_titles).
     */
    async getDraftImportAudit(
        campaignId: string,
        documentId: string
    ): Promise<{
        segments: Array<{
            title: string
            bodyPreview: string
            blockType: string
            blockTypeName: string
            matched: boolean
            slotsFound: string[]
        }>
        unmapped_segment_titles: string[]
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/draft-import-audit`, {
            params: { documentId },
        })
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * POST /campaigns/:id/landing-page/import-from-draft
     * documentId: optional; when omitted, uses campaign.landing_page_config.rough_draft_document_id or run.activate.rough_draft_document_id.
     */
    async importFromDraft(
        campaignId: string,
        options?: { documentId?: string; mode?: 'overwrite' | 'append' }
    ): Promise<{
        sections: Array<{ id: string; block_type: string; order: number; tfd_goal: string }>
        landing_page_config: Record<string, unknown>
    }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/import-from-draft`, {
            ...(options?.documentId ? { documentId: options.documentId } : {}),
            ...(options?.mode ? { mode: options.mode } : {}),
        })
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate block-by-block copy for landing page sections
     * POST /campaigns/:id/landing-page/generate-copy
     * contentGroupId: use this content group's ALOs; if not set, primary group or all campaign ALOs
     */
    async generateLandingPageCopy(
        campaignId: string,
        options?: {
            sectionIds?: string[]
            mode?: 'all' | 'sections' | 'unlocked_only'
            appendVariants?: boolean
            feedback?: string
            persuasionTypes?: string[]
            sectionOverrides?: Record<string, { generation_direction?: string }>
            contentGroupId?: string
        }
    ): Promise<{ sections: Array<Record<string, unknown>>; updated: boolean }> {
        // 7 sections × 2 variants each = 14+ sequential LLM calls (~10–15s each). Extended timeout.
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/generate-copy`, options || {}, {
            timeout: API_TIMEOUTS.EXTENDED_AI,
        })
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate landing page copy with progressive updates (SSE).
     * Calls onSection(section) as each section completes so the UI can update immediately.
     */
    async generateLandingPageCopyStreaming(
        campaignId: string,
        options: {
            sectionIds?: string[]
            mode?: 'all' | 'sections' | 'unlocked_only'
            appendVariants?: boolean
            feedback?: string
            persuasionTypes?: string[]
            sectionOverrides?: Record<string, { generation_direction?: string }>
            contentGroupId?: string
        },
        onSection: (section: Record<string, unknown>) => void
    ): Promise<{ sections: Array<Record<string, unknown>>; updated: boolean }> {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 240000)
        const url = `${NODE_BASE_URL}/campaigns/${campaignId}/landing-page/generate-copy`
        const res = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...options, stream: true }),
            signal: controller.signal,
        })
        clearTimeout(timeoutId)
        if (!res.ok) {
            const err = await res.json().catch(() => ({ error: res.statusText }))
            throw new Error((err as any)?.message || (err as any)?.error || 'Failed to generate copy')
        }
        const reader = res.body?.getReader()
        if (!reader) throw new Error('No response body')
        const decoder = new TextDecoder()
        let buffer = ''
        let finalResult: { sections: Array<Record<string, unknown>>; updated: boolean } | null = null
        let finalError: { error?: string; message?: string } | null = null
        const processLine = (line: string) => {
            if (!line.startsWith('data: ')) return
            try {
                const data = JSON.parse(line.slice(6)) as {
                    type: string
                    section?: Record<string, unknown>
                    sections?: Array<Record<string, unknown>>
                    updated?: boolean
                    error?: string
                    message?: string
                }
                if (data.type === 'section' && data.section) onSection(data.section)
                if (data.type === 'done') {
                    finalResult = { sections: data.sections ?? [], updated: data.updated ?? false }
                }
                if (data.type === 'error') {
                    finalError = { error: data.error, message: data.message }
                }
            } catch {
                // ignore parse errors for non-JSON lines
            }
        }
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const parts = buffer.split('\n\n')
            buffer = parts.pop() ?? ''
            for (const part of parts) {
                for (const line of part.split('\n')) processLine(line.trim())
            }
        }
        if (buffer) {
            for (const line of buffer.split('\n')) processLine(line.trim())
        }
        if (finalError)
            throw new Error((finalError as any).message || (finalError as any).error || 'Failed to generate copy')
        if (finalResult) return finalResult
        return { sections: [], updated: false }
    },

    /**
     * Select copy variant for a section
     * PATCH /campaigns/:id/landing-page/sections/:sectionId/select-variant
     */
    async selectLandingPageVariant(
        campaignId: string,
        sectionId: string,
        variantId: string
    ): Promise<{ success: boolean }> {
        const res = await NodeAxios.patch(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/select-variant`,
            { variantId }
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get copy status (green/yellow/red per gate)
     * GET /campaigns/:id/landing-page/copy-status
     */
    async getLandingPageCopyStatus(campaignId: string): Promise<{
        copyLab: 'green' | 'yellow' | 'red'
        preview: 'green' | 'yellow' | 'red'
        export: 'green' | 'yellow' | 'red'
        missing: Array<{ sectionId: string; reasons: string[]; linkMap?: Record<string, string> }>
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/copy-status`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate preview HTML (single-page Tailwind mockup)
     * POST /campaigns/:id/landing-page/generate-preview
     */
    async generateLandingPagePreview(campaignId: string): Promise<{
        html: string
        design_spec?: Record<string, unknown>
        composition_bias?: string
        layout_system_id?: string
        design_tier?: 'base' | 'enhanced'
    }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/generate-preview`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get copy context (Target, ALO, TFD, Goal, Binaries) — data driving copy
     * GET /campaigns/:id/landing-page/copy-context
     * Optional: groupId (content group id) or usePrimary (default true) to use primary content group's ALOs
     */
    async getLandingPageCopyContext(
        campaignId: string,
        options?: { groupId?: string; usePrimary?: boolean }
    ): Promise<{
        target: { name: string; description?: string } | null
        alos: string[]
        tfd: { think?: string; feel?: string; do?: string } | null
        goal: string
        persuasion: { primary: string[]; supporting: string[] }
        binaries: string[]
        messageRun?: { claimText: string; pillars: Array<{ id: string; text: string }> }
        canonicalSlotsByBlockType?: Record<string, string[]>
        copyBankOptionsAvailable?: boolean
    }> {
        const params = new URLSearchParams()
        if (options?.groupId) params.set('groupId', options.groupId)
        if (options?.usePrimary !== undefined) params.set('usePrimary', String(options.usePrimary))
        const qs = params.toString()
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/copy-context${qs ? `?${qs}` : ''}`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get Copy Bank assembled options per section (when message run + Copy Bank + blueprint snapshot exist).
     * GET /campaigns/:id/landing-page/copy-bank-options
     */
    async getLandingPageCopyBankOptions(campaignId: string): Promise<{
        copyBankOptions: Array<{ sectionId: string; options: Array<{ kind: string; text: string }> }>
        message?: string
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/copy-bank-options`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get block types that support image generation
     * GET /campaigns/:id/landing-page/image-slots
     */
    async getLandingPageImageSlots(campaignId: string): Promise<{ blocksWithImages: Record<string, string[]> }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/image-slots`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * GET /campaigns/:id/landing-page/image-archive
     * Returns all images ever generated for this landing page.
     */
    async getLandingPageImageArchive(campaignId: string): Promise<{
        archive: Array<{
            id: string
            url: string
            block_type?: string
            slot?: string
            visual_prompt?: string
            generated_at?: string | null
            meta?: { axis?: string }
        }>
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/image-archive`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? { archive: [] }
    },

    /**
     * POST /campaigns/:id/landing-page/sections/:sectionId/assign-image
     * Assigns an archived image URL to a section slot.
     */
    async assignLandingPageArchiveImage(
        campaignId: string,
        sectionId: string,
        payload: { url: string; slot?: string; visual_prompt?: string }
    ): Promise<{ sectionId: string; slot: string; url: string }> {
        const res = await NodeAxios.post(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/assign-image`,
            payload
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate Step 0 visual prompt for a section (target, goals, TFD, ALOs, copy → prompt)
     * POST /campaigns/:id/landing-page/sections/:sectionId/generate-visual-prompt
     */
    async generateLandingPageSectionVisualPrompt(
        campaignId: string,
        sectionId: string,
        options?: { slot?: string; instructions?: string }
    ): Promise<{ visualPrompt: string; slot: string }> {
        const res = await NodeAxios.post(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/generate-visual-prompt`,
            {
                ...(options?.slot && { slot: options.slot }),
                ...(options?.instructions && { instructions: options.instructions }),
            }
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate image for section (builds prompt, calls image API, saves to section)
     * POST /campaigns/:id/landing-page/sections/:sectionId/generate-image
     * Uses 90s timeout for DALL-E.
     */
    async generateLandingPageSectionImage(
        campaignId: string,
        sectionId: string,
        options?: { imageDirection?: string; replaceCandidateId?: string; slot?: string }
    ): Promise<{ url: string; slot: string; visualPrompt: string }> {
        const res = await NodeAxios.post(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/generate-image`,
            {
                ...(options?.imageDirection && { imageDirection: options.imageDirection }),
                ...(options?.replaceCandidateId && { replaceCandidateId: options.replaceCandidateId }),
                ...(options?.slot && { slot: options.slot }),
            },
            { timeout: API_TIMEOUTS.AI_GENERATION }
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Generate 2–3 image candidates (one per concept axis). Saves to section; first is selected.
     * POST /campaigns/:id/landing-page/sections/:sectionId/generate-image-candidates
     */
    async generateLandingPageSectionImageCandidates(
        campaignId: string,
        sectionId: string
    ): Promise<{
        queued?: boolean
        sectionId?: string
        slot?: string
        candidates?: Array<{ id: string; url: string; visual_prompt: string; meta?: { axis: string } }>
        selected_id?: string | null
    }> {
        const res = await NodeAxios.post(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/generate-image-candidates`,
            {},
            { timeout: API_TIMEOUTS.QUICK_OPERATION }
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Set selected image candidate (sticky). PATCH .../select-image-candidate
     */
    async selectLandingPageImageCandidate(
        campaignId: string,
        sectionId: string,
        candidateId: string
    ): Promise<{ selected_id: string; url: string; axis?: string }> {
        const res = await NodeAxios.patch(
            `/campaigns/${campaignId}/landing-page/sections/${sectionId}/select-image-candidate`,
            { candidateId }
        )
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get the visual prompts that will be sent to the visual PhD for each section/slot (confirm before generate).
     * GET /campaigns/:id/landing-page/visual-prompt-preview
     */
    async getLandingPageVisualPromptPreview(campaignId: string): Promise<{
        prompts: Array<{
            sectionId: string
            sectionLabel: string
            slot: string
            visual_prompt: string | null
            status: 'ready' | 'will_generate'
        }>
    }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/visual-prompt-preview`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Queue image generation for sections with image slots (async via Rabbit).
     * mode: 'all' | 'missing' — missing = only sections without a completed image.
     * POST /campaigns/:id/landing-page/generate-all-images
     */
    async generateLandingPageAllImages(
        campaignId: string,
        options?: { mode?: 'all' | 'missing' }
    ): Promise<{ queued: number; sectionIds: string[] }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/generate-all-images`, options || {})
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Save current landing page config as a version.
     * POST /campaigns/:id/landing-page/versions
     */
    async saveLandingPageVersion(
        campaignId: string,
        options?: { label?: string }
    ): Promise<{ version: { id: string; label?: string; created_at: string; snapshot: unknown } }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/versions`, options || {})
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * List saved landing page versions.
     * GET /campaigns/:id/landing-page/versions
     */
    async listLandingPageVersions(
        campaignId: string
    ): Promise<{ versions: Array<{ id: string; label?: string; created_at: string }> }> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/versions`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Restore a saved version as current landing_page_config.
     * POST /campaigns/:id/landing-page/versions/:versionId/restore
     */
    async restoreLandingPageVersion(campaignId: string, versionId: string): Promise<{ landing_page_config: unknown }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/landing-page/versions/${versionId}/restore`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Download landing page export bundle (ZIP: index.html, assets/, manifest.json).
     * GET /campaigns/:id/landing-page/export?versionId= optional
     */
    async downloadLandingPageExportBundle(campaignId: string, options?: { versionId?: string }): Promise<Blob> {
        const params = options?.versionId ? { versionId: options.versionId } : {}
        const res = await NodeAxios.get(`/campaigns/${campaignId}/landing-page/export`, {
            params,
            responseType: 'blob',
            timeout: API_TIMEOUTS.AI_GENERATION, // File processing (zip generation)
        })
        return (res as any)?.data ?? res
    },

    /**
     * Export campaign as ZIP (async — returns job_id, download via WebSocket export-ready)
     * POST /api/campaigns/:campaignId/export
     */
    async exportCampaignAsync(
        campaignId: string,
        options?: { statusSet?: string; includeImages?: boolean }
    ): Promise<{ job_id: string; status: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/export`, options || {})
        const body = (res as any).data
        return body?.data ?? body ?? res
    },

    /**
     * Generate campaign report (async — returns job_id, download via WebSocket report-ready)
     * POST /api/campaigns/:campaignId/reports/generate
     */
    async generateReport(
        campaignId: string,
        reportType: 'metadata' | 'targets' | 'alos' | 'topics' | 'full' | 'ga_tracking'
    ): Promise<{ job_id: string; report_type: string; status: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/reports/generate`, { reportType })
        const body = (res as any).data
        return body?.data ?? body ?? res
    },

    /**
     * Generate story read-back (narrative analysis) — async, returns job_id, listen for story-readback-ready
     * POST /api/campaigns/:campaignId/story-readback/generate
     */
    async generateStoryReadback(campaignId: string): Promise<{ job_id: string; status: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/story-readback/generate`)
        const body = (res as any).data
        return body?.data ?? body ?? res
    },

    /**
     * Get latest story read-back (cached narrative analysis)
     * GET /api/campaigns/:campaignId/story-readback
     * Returns null when no analysis exists (backend returns 200 + data: null).
     */
    async getStoryReadback(campaignId: string): Promise<any> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/story-readback`)
        const body = (res as any)?.data ?? res
        return body?.data ?? body ?? res
    },

    /**
     * Get prompt audit logs for a campaign
     * GET /api/campaigns/:campaignId/prompt-logs
     * @param opts - step, beatId, channel, limit
     */
    async getPromptLogs(
        campaignId: string,
        opts?: { step?: string; beatId?: string; channel?: string; limit?: number }
    ): Promise<{ success: boolean; logs: any[] }> {
        const filtered = opts && Object.fromEntries(Object.entries(opts).filter(([, v]) => v != null && v !== ''))
        const params =
            filtered && Object.keys(filtered).length > 0
                ? new URLSearchParams(filtered as Record<string, string>).toString()
                : ''
        const url = params ? `/campaigns/${campaignId}/prompt-logs?${params}` : `/campaigns/${campaignId}/prompt-logs`
        const response = await NodeAxios.get(url)
        return (response as any).data || response
    },

    /**
     * Run trace for content generation (build prompt, log it, skip AI)
     * POST /api/campaigns/:campaignId/audit/trace
     */
    async runContentTrace(
        campaignId: string,
        outlineAssetId: string,
        variationIndex?: number
    ): Promise<{ success: boolean; jobId?: string }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/audit/trace`, {
            outlineAssetId,
            variationIndex: variationIndex ?? 0,
        })
        return (response as any).data || response
    },

    /**
     * Clear prompt audit logs for a campaign
     * DELETE /api/campaigns/:campaignId/prompt-logs
     */
    async clearPromptLogs(campaignId: string): Promise<{ success: boolean }> {
        const response = await NodeAxios.delete(`/campaigns/${campaignId}/prompt-logs`)
        return (response as any).data || response
    },

    /**
     * Get all registered prompt blueprints (templates)
     * GET /api/campaigns/debug/blueprints
     */
    async getPromptBlueprints(): Promise<{ success: boolean; blueprints: any[] }> {
        const response = await NodeAxios.get('/campaigns/debug/blueprints')
        return (response as any).data || response
    },

    /**
     * Get command center data (all assets organized by beat)
     */
    async getCommandCenterData(campaignId: string): Promise<{
        campaign: Campaign
        beats: StoryBeat[]
        assets: any[]
        statistics: {
            totalAssets: number
            byStatus: Record<string, number>
            byBeat: Record<number, number>
            byChannel: Record<string, number>
            completionPercentage: number
            assetsWithUTMs: number
        }
        gaps: Array<{
            beat: number
            beatName: string
            severity: string
            message: string
        }>
    }> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/command-center`)
        return (response as any).data
    },

    /**
     * Preview UTM URL for single asset (no persistence)
     */
    async previewAssetUTM(
        campaignId: string,
        assetId: string
    ): Promise<{ previewUrl: string; utmParams: Record<string, string> }> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/assets/${assetId}/utm/preview`)
        return (response as any).data?.data ?? (response as any).data
    },

    /**
     * Generate UTM parameters for single asset
     */
    async generateAssetUTM(campaignId: string, assetId: string): Promise<{ asset: any }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/assets/${assetId}/utm/generate`)
        return (response as any).data
    },

    /**
     * Bulk generate UTMs for all assets
     */
    async bulkGenerateUTMs(campaignId: string): Promise<{ generated: number; skipped: number }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/assets/utm/generate-all`)
        const body = (response as any).data
        return body?.data ?? body ?? { generated: 0, skipped: 0 }
    },

    /**
     * Update campaign progress
     */
    async updateProgress(id: string, step: string): Promise<Campaign> {
        return await NodeAxios.patch(`/campaigns/${id}/progress`, { step })
    },

    /**
     * Update campaign status
     */
    async updateStatus(id: string, status: string): Promise<Campaign> {
        return await NodeAxios.patch(`/campaigns/${id}/status`, { status })
    },

    /**
     * Generate goals from website content using AI
     */
    async generateGoalsFromWebsite(params: {
        campaignId?: string
        website_url: string
        website_content: string
        additional_context?: string
        company_name?: string
    }): Promise<GoalGenerationResult> {
        return await NodeAxios.post('/campaigns/goals/generate', params, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
    },

    /**
     * Generate product profile from website
     */
    async generateProductFromWebsite(params: {
        website_url: string
        website_content: string
        additional_context?: string
    }): Promise<{ product: any; analyzed_url: string }> {
        return await NodeAxios.post('/campaigns/products/generate-from-website', params)
    },

    /**
     * Generate product profile from pasted content
     */
    async generateProductFromContent(params: {
        pasted_content: string
        additional_context?: string
    }): Promise<{ product: any }> {
        return await NodeAxios.post('/campaigns/products/generate-from-content', params)
    },

    /**
     * Generate company profile from website
     */
    async generateCompanyFromWebsite(params: {
        campaignId?: string
        website_url: string
        website_content: string
        additional_context?: string
    }): Promise<{ company: any; analyzed_url: string }> {
        return await NodeAxios.post('/campaigns/companies/generate-from-website', params)
    },

    /**
     * Generate company profile from content (website or pasted)
     */
    async generateCompanyFromContent(params: {
        campaignId?: string
        website_url?: string
        website_content?: string
        pasted_content?: string
        additional_context?: string
    }): Promise<{ company: any; analyzed_url?: string }> {
        return await NodeAxios.post('/campaigns/companies/generate-from-content', params)
    },

    /**
     * Generate person profile from website
     */
    async generatePersonFromWebsite(params: {
        website_url: string
        website_content: string
        additional_context?: string
    }): Promise<{ person: any; analyzed_url: string }> {
        return await NodeAxios.post('/campaigns/people/generate-from-website', params)
    },

    /**
     * Generate person profile from content (website or pasted)
     */
    async generatePersonFromContent(params: {
        website_url?: string
        website_content?: string
        pasted_content?: string
        additional_context?: string
    }): Promise<{ person: any; analyzed_url?: string }> {
        return await NodeAxios.post('/campaigns/people/generate-from-content', params)
    },

    /**
     * Upload a brand guide image or PDF to Spaces for AI analysis.
     * Images return a screenshotUrl that feeds directly into the 3 vision AI calls.
     * PDFs return extractedText that feeds into analyzeBrandGuideText.
     */
    async uploadBrandGuide(file: File): Promise<{
        success: boolean
        type: 'image' | 'pdf'
        screenshotUrl?: string
        fileUrl: string
        filename: string
        extractedText?: string | null
        pageCount?: number | null
    }> {
        const form = new FormData()
        form.append('file', file)
        return await NodeAxios.post('/campaigns/brand-vision/upload-guide', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    },

    /**
     * Analyze extracted PDF text from a brand guide in a single comprehensive AI call.
     * Returns identity, colors, and visual style in one shot.
     */
    async analyzeBrandGuideText(params: { text: string; filename?: string }): Promise<{
        success: boolean
        analysis: {
            identity: {
                name: string
                description: string
                tagline?: string
                category?: string
                brand_voice?: string
                key_messages?: string[]
                value_propositions?: string[]
                brand_personality?: string[]
                elevator_pitch?: string
                dos_and_donts?: Array<{ type: 'do' | 'dont'; description: string }>
            }
            colors: {
                primary_colors: Array<{
                    hex: string
                    name?: string
                    usage: string
                    prominence: 'primary' | 'secondary' | 'accent' | 'text' | 'button'
                }>
                color_palette: string[]
                dominant_color: string
            }
            visual_style: {
                design_style: string
                art_direction: string
                image_style: string
                typography_notes: string
                spacing_density: string
                visual_hierarchy: string
                ai_image_prompt: string
                include_elements?: string[]
                avoid_elements?: string[]
            }
        }
    }> {
        return await NodeAxios.post('/campaigns/brand-vision/analyze-guide-text', params)
    },

    /**
     * Analyze brand identity from screenshot using AI Vision
     */
    async analyzeBrandIdentity(params: { screenshot_url: string }): Promise<{
        success: boolean
        analysis: {
            name: string
            description: string
            tagline?: string
            category?: string
            brand_voice?: string
            key_messages?: string[]
            value_propositions?: string[]
            brand_personality?: string[]
            elevator_pitch?: string
            dos_and_donts?: Array<{
                type: 'do' | 'dont'
                description: string
            }>
        }
    }> {
        return await NodeAxios.post('/campaigns/brand-vision/analyze-identity', params)
    },

    /**
     * Analyze brand colors from screenshot using AI Vision
     */
    async analyzeBrandColors(params: { screenshot_url: string }): Promise<{
        success: boolean
        analysis: {
            primary_colors: Array<{
                hex: string
                name?: string
                usage: string
                prominence: 'primary' | 'secondary' | 'accent' | 'text' | 'button'
            }>
            color_palette: string[]
            dominant_color: string
        }
    }> {
        return await NodeAxios.post('/campaigns/brand-vision/analyze-colors', params)
    },

    /**
     * Analyze brand visual style from screenshot using AI Vision
     */
    async analyzeBrandVisualStyle(params: { screenshot_url: string }): Promise<{
        success: boolean
        analysis: {
            design_style: string
            art_direction: string
            image_style: string
            typography_notes: string
            spacing_density: string
            visual_hierarchy: string
            ai_image_prompt: string
            include_elements?: string[]
            avoid_elements?: string[]
        }
    }> {
        return await NodeAxios.post('/campaigns/brand-vision/analyze-visual-style', params)
    },

    /**
     * Save researched goals to campaign (with optional library save)
     * Matches the target pattern for consistency
     */
    async saveResearchedGoals(
        campaignId: string,
        params: {
            goals: Array<{
                name: string
                description?: string
                goal_type: 'product' | 'company' | 'people'
                metrics?: Array<{
                    name: string
                    target?: string
                    unit?: string
                    timeframe?: string
                }>
                success_criteria?: Array<{
                    description: string
                    measurable?: boolean
                }>
                messaging_themes?: string[]
                target_audience_context?: string
            }>
            save_to_library?: boolean
            inferred_type?: 'product' | 'company' | 'people'
            company_name?: string
        }
    ) {
        return await NodeAxios.post(`/campaigns/${campaignId}/goals/batch-create`, params)
    },

    /**
     * List library goals (workspace-level)
     */
    async listLibraryGoals(params?: {
        goal_type?: 'product' | 'company' | 'people'
        search?: string
        limit?: number
        skip?: number
        sort_by?: string
        sort_order?: 'asc' | 'desc'
    }): Promise<{ goals: LibraryGoal[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.goal_type) query.append('goal_type', params.goal_type)
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.sort_by) query.append('sort_by', params.sort_by)
        if (params?.sort_order) query.append('sort_order', params.sort_order)

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/goals${suffix}`)
    },

    async createLibraryGoal(data: {
        name: string
        description?: string
        goal_type: 'product' | 'company' | 'people'
        target_audience_context?: string
        messaging_themes?: string[]
    }): Promise<LibraryGoal> {
        return await NodeAxios.post(`/campaigns/library/goals`, data)
    },

    /**
     * Update a library goal
     */
    async updateLibraryGoal(goalId: string, data: Partial<LibraryGoal>): Promise<LibraryGoal> {
        return await NodeAxios.patch(`/campaigns/library/goals/${goalId}`, data)
    },

    /**
     * Delete a library goal
     */
    async deleteLibraryGoal(goalId: string): Promise<{ message: string }> {
        return await NodeAxios.delete(`/campaigns/library/goals/${goalId}`)
    },

    /**
     * Duplicate campaign
     */
    async duplicateCampaign(id: string, name: string): Promise<Campaign> {
        return await NodeAxios.post(`/campaigns/${id}/duplicate`, { name })
    },

    /**
     * Get workspace statistics
     */
    async getWorkspaceStats(): Promise<any> {
        return await NodeAxios.get('/campaigns/stats')
    },

    // ============================================
    // Topic Document API
    // ============================================

    /**
     * Create a Document for a topic and link it.
     * POST /api/campaigns/:campaignId/beats/:beatId/topics/:topicId/document
     */
    async createTopicDocument(
        campaignId: string,
        beatId: string,
        topicId: string,
        templateId: string
    ): Promise<{ document: any; alreadyExisted?: boolean }> {
        return (await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/topics/${topicId}/document`, {
            templateId,
        })) as any
    },

    /**
     * Get the Document linked to a topic.
     * GET /api/campaigns/:campaignId/beats/:beatId/topics/:topicId/document
     */
    async getTopicDocument(campaignId: string, beatId: string, topicId: string): Promise<{ document: any }> {
        return (await NodeAxios.get(`/campaigns/${campaignId}/beats/${beatId}/topics/${topicId}/document`)) as any
    },

    /**
     * Get the Document linked to a CampaignAsset.
     * GET /api/campaigns/:campaignId/assets/:assetId/document
     */
    async getAssetDocument(campaignId: string, assetId: string): Promise<{ document: any }> {
        return (await NodeAxios.get(`/campaigns/${campaignId}/assets/${assetId}/document`)) as any
    },

    /**
     * Get the resolved campaign context capsule (live strategic data).
     * GET /api/campaigns/:campaignId/context-capsule
     */
    async getCampaignContextCapsule(
        campaignId: string,
        params?: {
            beatId?: string
            targetId?: string
            aloIds?: string[]
            tfdGoalIds?: string[]
            channel?: string
            templateId?: string
            persuasionRunId?: string
        }
    ): Promise<{ capsule: any }> {
        const query = new URLSearchParams()
        if (params?.beatId) query.set('beatId', params.beatId)
        if (params?.targetId) query.set('targetId', params.targetId)
        if (params?.channel) query.set('channel', params.channel)
        if (params?.templateId) query.set('templateId', params.templateId)
        if (params?.persuasionRunId) query.set('persuasionRunId', params.persuasionRunId)
        params?.aloIds?.forEach(id => query.append('aloIds', id))
        params?.tfdGoalIds?.forEach(id => query.append('tfdGoalIds', id))
        const qs = query.toString() ? `?${query.toString()}` : ''
        return (await NodeAxios.get(`/campaigns/${campaignId}/context-capsule${qs}`)) as any
    },

    /**
     * Import a DraftArtifact into a topic document.
     * POST /api/campaigns/:campaignId/beats/:beatId/topics/:topicId/document/import-draft
     */
    async importDraftToTopicDocument(
        campaignId: string,
        beatId: string,
        topicId: string,
        draftArtifactId: string
    ): Promise<{ success: boolean; message?: string }> {
        return (await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/topics/${topicId}/document/import-draft`,
            { draftArtifactId }
        )) as any
    },
}

// ============================================
// Targets API
// ============================================

/**
 * Targets API
 *
 * Supports both Campaigns and Visual Identity Runs.
 * Pass isRun=true when working with Visual Engine runs.
 */
const getTargetPrefix = (id: string, isRun = false) => (isRun ? `/visual-engine/runs/${id}` : `/campaigns/${id}`)

export const targetsService = {
    /**
     * List targets for campaign or run
     */
    async listTargets(id: string, isRun = false): Promise<any[]> {
        return await NodeAxios.get(`${getTargetPrefix(id, isRun)}/targets`)
    },

    /**
     * Get library targets (all unique targets from workspace)
     */
    async getLibraryTargets(): Promise<CampaignTarget[]> {
        return await NodeAxios.get(`/campaigns/library/targets`)
    },

    /**
     * Create a new library target (workspace-level)
     */
    async createLibraryTarget(data: Partial<CampaignTarget>): Promise<CampaignTarget> {
        return await NodeAxios.post(`/campaigns/library/targets`, data)
    },

    /**
     * Update a library target
     */
    async updateLibraryTarget(targetId: string, data: Partial<CampaignTarget>): Promise<CampaignTarget> {
        return await NodeAxios.patch(`/campaigns/library/targets/${targetId}`, data)
    },

    /**
     * Delete a library target
     */
    async deleteLibraryTarget(targetId: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/campaigns/library/targets/${targetId}`)
    },

    /**
     * Get target by ID
     */
    async getTarget(id: string, targetId: string, isRun = false): Promise<CampaignTarget> {
        return await NodeAxios.get(`${getTargetPrefix(id, isRun)}/targets/${targetId}`)
    },

    /**
     * Create new target
     */
    async createTarget(id: string, data: Partial<CampaignTarget>, isRun = false): Promise<CampaignTarget> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets`, data)
    },

    /**
     * Add library targets to campaign or run
     */
    async addTargetsFromLibrary(
        id: string,
        libraryTargetIds: string[],
        asPrimary = false,
        isRun = false
    ): Promise<{
        success: boolean
        count: number
        targets: CampaignTarget[]
    }> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets/from-library`, {
            libraryTargetIds,
            asPrimary,
        })
    },

    /**
     * Update target
     */
    async updateTarget(
        id: string,
        targetId: string,
        data: Partial<CampaignTarget>,
        isRun = false
    ): Promise<CampaignTarget> {
        return await NodeAxios.patch(`${getTargetPrefix(id, isRun)}/targets/${targetId}`, data)
    },

    /**
     * Delete target
     */
    async deleteTarget(id: string, targetId: string, isRun = false): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`${getTargetPrefix(id, isRun)}/targets/${targetId}`)
    },

    /**
     * Save campaign target to library
     */
    async saveTargetToLibrary(id: string, targetId: string, isRun = false): Promise<any> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets/${targetId}/save-to-library`)
    },

    /**
     * Bulk delete targets from campaign or run
     */
    async bulkDeleteTargets(
        id: string,
        targetIds: string[],
        isRun = false
    ): Promise<{ success: boolean; deletedCount: number; errors?: string[] }> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets/bulk-delete`, { targetIds })
    },

    /**
     * Bulk delete library targets
     */
    async bulkDeleteLibraryTargets(
        targetIds: string[]
    ): Promise<{ success: boolean; deletedCount: number; errors?: string[] }> {
        return await NodeAxios.post(`/campaigns/library/targets/bulk-delete`, { targetIds })
    },

    /**
     * Update TFD outcomes
     */
    async updateTFDGoals(id: string, targetId: string, tfdGoals: any, isRun = false): Promise<CampaignTarget> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets/${targetId}/tfd`, { tfd_goals: tfdGoals })
    },

    /**
     * Save campaign TFD to library
     */
    async saveTFDToLibrary(id: string, targetId: string, isRun = false): Promise<any> {
        return await NodeAxios.post(`${getTargetPrefix(id, isRun)}/targets/${targetId}/tfd/save-to-library`)
    },

    /**
     * Generate TFD outcomes for a target using AI
     */
    async generateTFDGoals(id: string, targetId: string, force?: boolean, isRun = false): Promise<CampaignTarget> {
        const prefix = getTargetPrefix(id, isRun)
        const result = await NodeAxios.post(`${prefix}/targets/${targetId}/generate-tfd`, {
            force: force || false,
        })
        return result.data || ((result as any).target as CampaignTarget)
    },

    /**
     * Regenerate TFD outcomes (force regeneration even if edited)
     */
    async regenerateTFDGoals(id: string, targetId: string, isRun = false): Promise<CampaignTarget> {
        const prefix = getTargetPrefix(id, isRun)
        const result = await NodeAxios.post(`${prefix}/targets/${targetId}/regenerate-tfd`)
        return result.data || ((result as any).target as CampaignTarget)
    },

    /**
     * Batch generate TFD outcomes for multiple targets
     */
    async batchGenerateTFDGoals(
        id: string,
        targetIds: string[],
        isRun = false
    ): Promise<{ success: number; failed: number; errors: string[] }> {
        const prefix = getTargetPrefix(id, isRun)
        const result = await NodeAxios.post(`${prefix}/targets/batch-generate-tfd`, { targetIds })
        return (result as any).results
    },

    /**
     * Generate TFD outcomes with custom prompt (level-specific)
     */
    async generateTFDGoalsCustom(
        id: string,
        targetId: string,
        customPrompt: string,
        isRun = false,
        force = false,
        attentionLevel?: string
    ): Promise<CampaignTarget> {
        const prefix = getTargetPrefix(id, isRun)
        const result = await NodeAxios.post(`${prefix}/targets/${targetId}/generate-tfd-custom`, {
            customPrompt,
            force,
            attentionLevel, // Pass the attention level to backend
        })
        return (result as any).target as CampaignTarget
    },

    /**
     * Update TFD outcomes (manual edit) - marks as user_edited
     */
    async updateTFDGoalsManual(
        id: string,
        targetId: string,
        tfdGoals: {
            think?: string
            feel?: string
            do?: string
            attention_levels?: Array<{
                level: string
                think: string
                feel: string
                do: string
            }>
        },
        isRun = false
    ): Promise<CampaignTarget> {
        const prefix = getTargetPrefix(id, isRun)
        const result = await NodeAxios.patch(`${prefix}/targets/${targetId}/tfd`, tfdGoals)
        return (result as any).target as CampaignTarget
    },

    /**
     * Lock/unlock target (approve for content creation)
     */
    async toggleLockTarget(id: string, targetId: string, isLocked: boolean, isRun = false): Promise<CampaignTarget> {
        const prefix = getTargetPrefix(id, isRun)
        return await NodeAxios.patch(`${prefix}/targets/${targetId}/lock`, { is_locked: isLocked })
    },

    /**
     * Get target analytics
     */
    async getTargetAnalytics(id: string, targetId: string, isRun = false): Promise<any> {
        const prefix = getTargetPrefix(id, isRun)
        return await NodeAxios.get(`${prefix}/targets/${targetId}/analytics`)
    },

    /**
     * Bulk lock targets
     */
    async bulkLockTargets(
        id: string,
        targetIds: string[],
        isLocked: boolean,
        isRun = false
    ): Promise<{ count: number }> {
        const prefix = getTargetPrefix(id, isRun)
        return await NodeAxios.post(`${prefix}/targets/bulk-lock`, { targetIds, is_locked: isLocked })
    },
}

// ============================================
// ALOs API
// ============================================

export const aldsService = {
    /**
     * List ALOs for campaign
     */
    async listALDs(campaignId: string, filters?: { type?: string; status?: string }): Promise<CampaignALO[]> {
        const params = new URLSearchParams()
        if (filters?.type) params.append('type', filters.type)
        if (filters?.status) params.append('status', filters.status)

        const queryString = params.toString()
        return await NodeAxios.get(`/campaigns/${campaignId}/alos${queryString ? `?${queryString}` : ''}`)
    },

    /**
     * Get ALD by ID
     */
    async getALD(campaignId: string, aldId: string): Promise<CampaignALO> {
        return await NodeAxios.get(`/campaigns/${campaignId}/alos/${aldId}`)
    },

    /**
     * Create new ALD
     */
    async createALD(campaignId: string, data: Partial<CampaignALO>): Promise<CampaignALO> {
        return await NodeAxios.post(`/campaigns/${campaignId}/alos`, data)
    },

    /**
     * Update ALD
     */
    async updateALD(campaignId: string, aldId: string, data: Partial<CampaignALO>): Promise<CampaignALO> {
        return await NodeAxios.patch(`/campaigns/${campaignId}/alos/${aldId}`, data)
    },

    /**
     * Update ALD status
     */
    async updateALOStatus(campaignId: string, aldId: string, status: string): Promise<CampaignALO> {
        return await NodeAxios.patch(`/campaigns/${campaignId}/alos/${aldId}/status`, { status })
    },

    /**
     * Delete ALD
     */
    async deleteALD(campaignId: string, aldId: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/campaigns/${campaignId}/alos/${aldId}`)
    },

    /**
     * Bulk approve ALDs
     */
    async bulkApprove(campaignId: string, aldIds: string[]): Promise<{ approved_count: number }> {
        return await NodeAxios.post(`/campaigns/${campaignId}/alos/bulk-approve`, { aldIds })
    },
}

// ============================================
// ALO Groups API (New System)
// ============================================

export const aloGroupsService = {
    /**
     * Get all ALO groups for a campaign
     */
    async getGroups(campaignId: string): Promise<{
        attractants: CampaignALO[]
        assets: CampaignALO[]
        liabilities: CampaignALO[]
        omissions: CampaignALO[]
    }> {
        const result = await NodeAxios.get(`/campaigns/${campaignId}/alos/groups`)
        return result.data
    },

    /**
     * Create ALD group
     */
    async createGroup(
        campaignId: string,
        data: {
            level: 'company' | 'product' | 'people'
            assignedToType?: 'product' | 'person'
            assignedToId?: string
            assignedToName?: string
            loadFromLibrary?: boolean
            libraryGroupId?: string
        }
    ): Promise<ALOGroup> {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/alos/groups`, data)
        return result.data
    },

    /**
     * Update ALD group
     */
    async updateGroup(
        campaignId: string,
        groupId: string,
        data: {
            assignedToName?: string
            tags?: string[]
        }
    ): Promise<ALOGroup> {
        const result = await NodeAxios.patch(`/campaigns/${campaignId}/alos/groups/${groupId}`, data)
        return result.data
    },

    /**
     * Delete ALD group
     */
    async deleteGroup(campaignId: string, groupId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/${campaignId}/alos/groups/${groupId}`)
    },

    /**
     * Get ALDs for a group
     */
    async getALDsForGroup(
        campaignId: string,
        groupId: string
    ): Promise<{
        attractants: CampaignALO[]
        assets: CampaignALO[]
        liabilities: CampaignALO[]
        omissions: CampaignALO[]
    }> {
        const result = await NodeAxios.get(`/campaigns/${campaignId}/alos/groups/${groupId}/alos`)
        const parsed = parseALOsByTypePayload(result)
        return {
            attractants: parsed.attractants as CampaignALO[],
            assets: parsed.attractants as CampaignALO[],
            liabilities: parsed.liabilities as CampaignALO[],
            omissions: parsed.omissions as CampaignALO[],
        }
    },

    /**
     * Add ALDs to a group
     */
    async addALDs(
        campaignId: string,
        groupId: string,
        alds: Array<{
            type: 'asset' | 'liability' | 'omission'
            category: string
            text: string
            description?: string
            isStrategic?: boolean
            priority?: number
            proof_points?: string[] // ✅ Fixed to snake_case
            proofUrl?: string // ✅ ADDED
            proofStrength?: 'strong' | 'medium' | 'weak' // ✅ ADDED
            persuasion_type?: 'ethos' | 'pathos' | 'logos' | 'kairos' | 'other' // ✅ ADDED
            rhetoric_devices?: string[] // ✅ ADDED
            related_products?: string[] // ✅ ADDED
            confidence?: number // ✅ ADDED
        }>
    ): Promise<CampaignALO[]> {
        const result = await NodeAxios.post(`/campaigns/${campaignId}/alos/groups/${groupId}/alos`, { alds })
        const saved = parseAddALOsResponse(result)
        if (saved.length === 0 && alds.length > 0) {
            console.error('[aloGroupsService.addALDs] Unexpected empty response:', result)
        }
        return saved as CampaignALO[]
    },

    /**
     * Update individual ALD
     */
    async updateALD(
        campaignId: string,
        aldId: string,
        data: {
            text?: string
            description?: string
            isStrategic?: boolean
            priority?: number
            included?: boolean
            proof_points?: string[] // ✅ Fixed to snake_case to match backend
            proofUrl?: string
            proofStrength?: 'strong' | 'medium' | 'weak'
            category?: string
        }
    ): Promise<CampaignALO> {
        const result = await NodeAxios.patch(`/campaigns/${campaignId}/alos/${aldId}/new`, data)
        return result.data
    },

    /**
     * Toggle ALD inclusion
     */
    async toggleALD(campaignId: string, aldId: string, included: boolean): Promise<CampaignALO> {
        const result = await NodeAxios.patch(`/campaigns/${campaignId}/alos/${aldId}/toggle`, { included })
        return result.data
    },

    /**
     * Delete individual ALD
     */
    async deleteALD(campaignId: string, aldId: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/campaigns/${campaignId}/alos/${aldId}/new`)
    },
}

// ============================================
// ALO Content Groups API (user-defined ALO sets for landing page / narrative)
// ============================================

export const aloContentGroupsService = {
    async list(campaignId: string): Promise<{ name: string; description?: string; isPrimary?: boolean }[]> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/alos/content-groups`)
        const data = (res as any)?.data?.data ?? (res as any)?.data ?? res
        return Array.isArray(data) ? data : []
    },

    async create(
        campaignId: string,
        data: { name: string; description?: string; aloIds?: string[] }
    ): Promise<{ name: string; description?: string; isPrimary?: boolean }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/alos/content-groups`, data)
        return (res as any)?.data?.data ?? (res as any)?.data ?? res
    },

    async update(
        campaignId: string,
        groupId: string,
        data: { name?: string; description?: string; isPrimary?: boolean }
    ): Promise<{ name: string; description?: string; isPrimary?: boolean }> {
        const res = await NodeAxios.patch(`/campaigns/${campaignId}/alos/content-groups/${groupId}`, data)
        return (res as any)?.data?.data ?? (res as any)?.data ?? res
    },

    async delete(campaignId: string, groupId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/${campaignId}/alos/content-groups/${groupId}`)
    },

    async addALOs(
        campaignId: string,
        groupId: string,
        aloIds: string[]
    ): Promise<{ name: string; description?: string; isPrimary?: boolean }[]> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/alos/content-groups/${groupId}/alos`, { aloIds })
        return (res as any)?.data?.data ?? (res as any)?.data ?? res
    },

    async removeALO(
        campaignId: string,
        groupId: string,
        aloId: string
    ): Promise<{ name: string; description?: string; isPrimary?: boolean }> {
        const res = await NodeAxios.delete(`/campaigns/${campaignId}/alos/content-groups/${groupId}/alos/${aloId}`)
        return (res as any)?.data?.data ?? (res as any)?.data ?? res
    },

    async getALOs(campaignId: string, groupId: string): Promise<CampaignALO[]> {
        const res = await NodeAxios.get(`/campaigns/${campaignId}/alos/content-groups/${groupId}/alos`)
        const data = (res as any)?.data?.data ?? (res as any)?.data ?? res
        return Array.isArray(data) ? data : []
    },

    /**
     * AI: select the strongest ALOs from a candidate list for landing page goals.
     * Returns { selectedAloIds, reasoning? }.
     */
    async pickStrongestForLanding(
        campaignId: string,
        aloIds: string[]
    ): Promise<{ selectedAloIds: string[]; reasoning?: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/alos/select-strongest-for-landing`, { aloIds })
        const data = (res as any)?.data?.data ?? (res as any)?.data ?? res
        return {
            selectedAloIds: Array.isArray(data?.selectedAloIds) ? data.selectedAloIds : [],
            reasoning: data?.reasoning,
        }
    },

    // ─── Story Hub Storyboard Integration (Phase 3) ───

    /** Import a Story Hub storyboard into an influencer campaign, creating beats from frames. */
    async importStoryboard(
        campaignId: string,
        payload: { storyboard_id: string; influencer_archetype?: string; influencer_attention_level?: string }
    ): Promise<{ ok: boolean; beat_count: number; message: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/import-storyboard`, payload)
        return (res as any).data ?? res
    },

    /** Fan-out: queue influencer moment generation for all storyboard-derived beats. */
    async generateAllInfluencerMoments(
        campaignId: string
    ): Promise<{ success: boolean; queued: number; total?: number }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/moments`)
        return (res as any).data ?? res
    },
}

// ============================================
// Story Beats API
// ============================================

export const beatsService = {
    /**
     * List beats for campaign
     */
    async listBeats(campaignId: string): Promise<StoryBeat[]> {
        return await NodeAxios.get(`/campaigns/${campaignId}/beats`)
    },

    /**
     * Get beat by ID
     */
    async getBeat(campaignId: string, beatId: string): Promise<StoryBeat> {
        return await NodeAxios.get(`/campaigns/${campaignId}/beats/${beatId}`)
    },

    /**
     * Create new beat
     */
    async createBeat(campaignId: string, data: Partial<StoryBeat>): Promise<StoryBeat> {
        return await NodeAxios.post(`/campaigns/${campaignId}/beats`, data)
    },

    /**
     * Generate beats from framework
     */
    async generateBeats(campaignId: string, framework: string): Promise<{ beats: StoryBeat[] }> {
        return await NodeAxios.post(`/campaigns/${campaignId}/beats/generate`, { framework })
    },

    /**
     * Update beat
     */
    async updateBeat(campaignId: string, beatId: string, data: Partial<StoryBeat>): Promise<StoryBeat> {
        return await NodeAxios.patch(`/campaigns/${campaignId}/beats/${beatId}`, data)
    },

    /**
     * Delete beat
     */
    async deleteBeat(campaignId: string, beatId: string): Promise<{ success: boolean }> {
        return await NodeAxios.delete(`/campaigns/${campaignId}/beats/${beatId}`)
    },

    /**
     * Mark beat as complete
     */
    async markComplete(campaignId: string, beatId: string, isComplete: boolean): Promise<StoryBeat> {
        return await NodeAxios.patch(`/campaigns/${campaignId}/beats/${beatId}/complete`, { is_complete: isComplete })
    },

    /** Queue influencer moment generation for a single storyboard-derived beat. */
    async generateInfluencerMoment(campaignId: string, beatId: string): Promise<{ ok: boolean; message: string }> {
        const res = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/moment`)
        return (res as any).data ?? res
    },

    /** Save influencer_moment edits for a beat. */
    async saveInfluencerMoment(
        campaignId: string,
        beatId: string,
        moment: {
            moment_direction?: string
            character_note?: string
            talking_points?: string[]
            brand_handles?: string[]
        }
    ): Promise<StoryBeat> {
        const res = await NodeAxios.patch(`/campaigns/${campaignId}/beats/${beatId}`, {
            influencer_moment: { ...moment, is_edited: true },
        })
        return (res as any).data ?? res
    },
}

// ============================================
// Library Products API
// ============================================

export const libraryProductsService = {
    /**
     * List library products
     */
    async listProducts(params?: {
        category?: string
        tags?: string[]
        search?: string
        limit?: number
        skip?: number
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
    }): Promise<{ products: LibraryProduct[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.category) query.append('category', params.category)
        if (params?.tags?.length) query.append('tags', params.tags.join(','))
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.sortBy) query.append('sortBy', params.sortBy)
        if (params?.sortOrder) query.append('sortOrder', params.sortOrder)

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/products${suffix}`)
    },

    /**
     * Get a library product by ID
     */
    async getProduct(productId: string, config?: { skipErrorToast?: boolean }): Promise<LibraryProduct> {
        return await NodeAxios.get(`/campaigns/library/products/${productId}`, config)
    },

    /**
     * Create a new library product
     */
    async createProduct(data: Partial<LibraryProduct>): Promise<LibraryProduct> {
        return await NodeAxios.post(`/campaigns/library/products`, data)
    },

    /**
     * Update a library product
     */
    async updateProduct(productId: string, data: Partial<LibraryProduct>): Promise<LibraryProduct> {
        return await NodeAxios.patch(`/campaigns/library/products/${productId}`, data)
    },

    /**
     * Delete a library product
     */
    async deleteProduct(productId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/library/products/${productId}`)
    },

    /**
     * Get library product stats
     */
    async getStats(): Promise<{
        total: number
        by_category: Record<string, number>
        most_used: LibraryProduct[]
    }> {
        return await NodeAxios.get(`/campaigns/library/products/stats`)
    },
}

// ============================================
// Library Groups API
// ============================================

export const libraryGroupsService = {
    async listGroups(params?: { search?: string; limit?: number }): Promise<{ groups: LibraryGroup[] }> {
        const query = new URLSearchParams()
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/groups${suffix}`)
    },
    async getGroup(groupId: string): Promise<LibraryGroup> {
        return await NodeAxios.get(`/campaigns/library/groups/${groupId}`)
    },
    async createGroup(input: Partial<LibraryGroup>): Promise<LibraryGroup> {
        return await NodeAxios.post('/campaigns/library/groups', input)
    },
    async updateGroup(groupId: string, input: Partial<LibraryGroup>): Promise<LibraryGroup> {
        return await NodeAxios.patch(`/campaigns/library/groups/${groupId}`, input)
    },
    async deleteGroup(groupId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/library/groups/${groupId}`)
    },
}

// ============================================
// Library Stories API
// ============================================

export const libraryStoryService = {
    /**
     * List library stories for a subject
     */
    async listStories(params: {
        subjectId?: string
        subjectType?: 'person' | 'company' | 'product' | 'group'
        search?: string
        limit?: number
        skip?: number
        skipErrorToast?: boolean
    }): Promise<{ success: boolean; stories: LibraryStory[]; total: number }> {
        const query = new URLSearchParams()
        if (params.subjectId) query.append('subjectId', params.subjectId)
        if (params.subjectType) query.append('subjectType', params.subjectType)
        if (params.search) query.append('search', params.search)
        if (params.limit) query.append('limit', String(params.limit))
        if (params.skip) query.append('skip', String(params.skip))

        const suffix = query.toString() ? `?${query.toString()}` : ''
        const result = (await NodeAxios.get(`/campaigns/library/stories${suffix}`, {
            ...(params.skipErrorToast ? { skipErrorToast: true } : {}),
        })) as {
            success?: boolean
            stories?: LibraryStory[]
            total?: number
            data?: { stories?: LibraryStory[]; total?: number }
        }
        const stories = result.stories ?? result.data?.stories ?? []
        return {
            success: result.success ?? true,
            stories,
            total: result.total ?? result.data?.total ?? stories.length,
        }
    },

    /**
     * Get a library story by ID
     */
    async getStory(storyId: string): Promise<{ success: boolean; story: LibraryStory }> {
        const result = await NodeAxios.get(`/campaigns/library/stories/${storyId}`)
        return result.data || result
    },

    /**
     * Create a new library story
     */
    async createStory(data: Partial<LibraryStory>): Promise<{ success: boolean; story: LibraryStory }> {
        const result = await NodeAxios.post('/campaigns/library/stories', {
            ...data,
            editIntent: 'presentation',
            compatibilityWrite: true,
        })
        return result.data || result
    },

    /**
     * Generate a story from content
     */
    async generateStory(params: {
        content: string
        subjectName: string
        subjectType: 'person' | 'company' | 'product' | 'group'
        additionalContext?: string
    }): Promise<{ success: boolean; data: any }> {
        const result = (await NodeAxios.post('/campaigns/library/stories/generate', params, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })) as any
        // This endpoint returns { success, data }. Do NOT unwrap `.data` here:
        // callers (e.g. StoryResearchModal) expect the wrapper to check success + read `.data`.
        return result
    },

    /**
     * Refine or generate a specific story version based on direction
     */
    async refineStoryVersion(params: {
        story: any
        subjectName: string
        subjectType: string
        direction: string
        versionType?: string
    }): Promise<{ success: boolean; content: string }> {
        const result = await NodeAxios.post('/campaigns/library/stories/refine-version', params, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        return result.data || result
    },

    /**
     * Generate standard story versions from FETS/HEFT data
     */
    async generateStoryVersions(params: {
        fset: any
        heft: any
        subjectName: string
        subjectType: string
    }): Promise<{ success: boolean; versions: any[] }> {
        const result = await NodeAxios.post('/campaigns/library/stories/generate-versions', params, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        return result.data || result
    },

    /**
     * Update a library story
     */
    async updateStory(
        storyId: string,
        data: Partial<LibraryStory> & Record<string, unknown>
    ): Promise<{ success: boolean; story: LibraryStory }> {
        const result = await NodeAxios.patch(`/campaigns/library/stories/${storyId}`, data)
        return result.data || result
    },

    /**
     * Delete a library story
     */
    async deleteStory(
        storyId: string,
        data: { editIntent: 'presentation'; compatibilityWrite: true } = {
            editIntent: 'presentation',
            compatibilityWrite: true,
        }
    ): Promise<{ success: boolean; message: string; editIntent?: 'presentation'; compatibilityWrite?: true }> {
        const result = await NodeAxios.delete(`/campaigns/library/stories/${storyId}`, { data })
        return result.data || result
    },

    /**
     * Merge multiple stories into one (guided regeneration)
     */
    async mergeStories(params: {
        storyIds: string[]
        subjectId: string
        subjectType: 'person' | 'company' | 'product' | 'group'
        subjectName?: string
    }): Promise<{ success: boolean; storyId: string }> {
        const result = await NodeAxios.post(
            '/campaigns/library/stories/merge',
            {
                ...params,
                editIntent: 'presentation',
                compatibilityWrite: true,
            },
            {
                timeout: 120000,
            }
        )
        return result.data || result
    },

    /**
     * Update story lifecycle status (staged → active, discarded, etc.)
     */
    async patchStoryStatus(
        storyId: string,
        status: 'staged' | 'active' | 'superseded' | 'discarded'
    ): Promise<{ success: boolean }> {
        const result = await NodeAxios.patch(`/campaigns/library/stories/${storyId}/status`, {
            status,
            editIntent: 'usage',
            compatibilityWrite: true,
        })
        return result.data || result
    },

    /**
     * List stories created from a specific interview session (for staged curation)
     */
    async listStoriesBySession(
        sessionId: string
    ): Promise<{ success: boolean; stories: LibraryStory[]; total: number }> {
        const result = await NodeAxios.get(`/campaigns/library/stories?sessionId=${sessionId}`)
        return result.data || result
    },

    /**
     * HEFT-specific merge: merge staged stories from a curation flow
     */
    async heftMergeStories(storyIds: string[], sessionId?: string): Promise<{ success: boolean; story: LibraryStory }> {
        const result = await NodeAxios.post(
            '/campaigns/library/stories/heft-merge',
            { storyIds, sessionId },
            {
                timeout: 120000,
            }
        )
        return result.data || result
    },
}

// ============================================
// Library People API
// ============================================

export const libraryPeopleService = {
    /**
     * List library people
     */
    async listPeople(params?: {
        department?: string
        tags?: string[]
        search?: string
        limit?: number
        skip?: number
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
    }): Promise<{ people: LibraryPerson[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.department) query.append('department', params.department)
        if (params?.tags?.length) query.append('tags', params.tags.join(','))
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.sortBy) query.append('sortBy', params.sortBy)
        if (params?.sortOrder) query.append('sortOrder', params.sortOrder)

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/people${suffix}`)
    },

    /**
     * Get a library person by ID
     */
    async getPerson(personId: string, config?: { skipErrorToast?: boolean }): Promise<LibraryPerson> {
        return await NodeAxios.get(`/campaigns/library/people/${personId}`, config)
    },

    /**
     * Create a new library person
     */
    async createPerson(data: Partial<LibraryPerson>): Promise<LibraryPerson> {
        return await NodeAxios.post(`/campaigns/library/people`, data)
    },

    /**
     * Update a library person
     */
    async updatePerson(personId: string, data: Partial<LibraryPerson>): Promise<LibraryPerson> {
        return await NodeAxios.patch(`/campaigns/library/people/${personId}`, data)
    },

    /**
     * Delete a library person
     */
    async deletePerson(personId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/library/people/${personId}`)
    },

    /**
     * Get library person stats
     */
    async getStats(): Promise<{
        total: number
        by_department: Record<string, number>
        most_used: LibraryPerson[]
    }> {
        return await NodeAxios.get(`/campaigns/library/people/stats`)
    },
}

// ============================================
// Library Companies API
// ============================================

export const libraryCompaniesService = {
    /**
     * List library companies
     */
    async listCompanies(params?: {
        industry?: string
        tags?: string[]
        search?: string
        limit?: number
        skip?: number
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
    }): Promise<{ companies: LibraryCompany[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.industry) query.append('industry', params.industry)
        if (params?.tags?.length) query.append('tags', params.tags.join(','))
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.sortBy) query.append('sortBy', params.sortBy)
        if (params?.sortOrder) query.append('sortOrder', params.sortOrder)

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/companies${suffix}`)
    },

    /**
     * Get a library company by ID
     */
    async getCompany(companyId: string, config?: { skipErrorToast?: boolean }): Promise<LibraryCompany> {
        return await NodeAxios.get(`/campaigns/library/companies/${companyId}`, config)
    },

    /**
     * Get the default company for the organization
     */
    async getDefaultCompany(): Promise<LibraryCompany> {
        return await NodeAxios.get(`/campaigns/library/companies/default`)
    },

    /**
     * Create a new library company
     */
    async createCompany(data: Partial<LibraryCompany>): Promise<LibraryCompany> {
        return await NodeAxios.post(`/campaigns/library/companies`, data)
    },

    /**
     * Update a library company
     */
    async updateCompany(companyId: string, data: Partial<LibraryCompany>): Promise<LibraryCompany> {
        return await NodeAxios.patch(`/campaigns/library/companies/${companyId}`, data)
    },

    /**
     * Set a company as the default
     */
    async setDefaultCompany(companyId: string): Promise<LibraryCompany> {
        return await NodeAxios.post(`/campaigns/library/companies/${companyId}/set-default`, {})
    },

    /**
     * Delete a library company
     */
    async deleteCompany(companyId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/library/companies/${companyId}`)
    },

    /**
     * Get library company stats
     */
    async getStats(): Promise<{
        total: number
        by_industry: Array<{ _id: string; count: number }>
        most_used: LibraryCompany[]
        has_default: boolean
    }> {
        return await NodeAxios.get(`/campaigns/library/companies/stats`)
    },
}

// ============================================
// Communication Archetype Suggestion (Library)
// ============================================

export type AssetType = 'company' | 'product' | 'person'

export const archetypeSuggestService = {
    async suggest(params: {
        assetType: AssetType
        assetName?: string
        input: string
        existingArchetype?: string | null
    }): Promise<{ suggested: string | null; alternates: string[]; rationale: string | null }> {
        const res = await NodeAxios.post<{ suggested: string | null; alternates: string[]; rationale: string | null }>(
            '/campaigns/library/archetype-suggest',
            {
                assetType: params.assetType,
                assetName: params.assetName ?? undefined,
                input: params.input,
                existingArchetype: params.existingArchetype ?? undefined,
            }
        )
        const data = (res as any)?.data ?? res
        return data ?? { suggested: null, alternates: [], rationale: null }
    },
}

// ============================================
// Brand Assets (Library)
// ============================================

export const brandAssetService = {
    /**
     * List brand assets
     */
    async listBrandAssets(params?: {
        category?: string
        tags?: string[]
        search?: string
        limit?: number
        skip?: number
        sortBy?: string
        sortOrder?: 'asc' | 'desc'
    }): Promise<{ brandAssets: BrandAsset[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.category) query.append('category', params.category)
        if (params?.tags?.length) query.append('tags', params.tags.join(','))
        if (params?.search) query.append('search', params.search)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.sortBy) query.append('sortBy', params.sortBy)
        if (params?.sortOrder) query.append('sortOrder', params.sortOrder)

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/library/brand-assets${suffix}`)
    },

    /**
     * Get a brand asset by ID
     */
    async getBrandAsset(brandAssetId: string): Promise<BrandAsset> {
        const response = await NodeAxios.get(`/campaigns/library/brand-assets/${brandAssetId}`)
        return (response as any).data || response
    },

    /**
     * Get the default brand asset for the organization
     */
    async getDefaultBrandAsset(): Promise<BrandAsset> {
        return await NodeAxios.get(`/campaigns/library/brand-assets/default`)
    },

    /**
     * Create a new brand asset
     */
    async createBrandAsset(data: Partial<BrandAsset>): Promise<BrandAsset> {
        return await NodeAxios.post(`/campaigns/library/brand-assets`, data)
    },

    /**
     * Update a brand asset
     */
    async updateBrandAsset(brandAssetId: string, data: Partial<BrandAsset>): Promise<BrandAsset> {
        return await NodeAxios.patch(`/campaigns/library/brand-assets/${brandAssetId}`, data)
    },

    /**
     * Set a brand asset as the default
     */
    async setDefaultBrandAsset(brandAssetId: string): Promise<BrandAsset> {
        return await NodeAxios.post(`/campaigns/library/brand-assets/${brandAssetId}/set-default`, {})
    },

    /**
     * Delete a brand asset
     */
    async deleteBrandAsset(brandAssetId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/library/brand-assets/${brandAssetId}`)
    },

    /**
     * Track brand asset usage in a campaign
     */
    async trackBrandAssetUsage(brandAssetId: string, campaignId: string): Promise<{ success: boolean }> {
        return await NodeAxios.post(`/campaigns/library/brand-assets/${brandAssetId}/track-usage`, { campaignId })
    },

    /**
     * Research brand identity for AI generation settings
     */
    async researchBrandIdentity(context: string): Promise<any> {
        const response = await NodeAxios.post(`/campaigns/library/brand-assets/research-identity`, { context })
        return response.data || response
    },

    /**
     * Upload a profile image to a brand asset (multipart)
     */
    async uploadProfileImage(
        brandAssetId: string,
        file: File,
        kind: string,
        label?: string,
        profileContext?: { profileType?: string; profileName?: string }
    ): Promise<{ id: string; kind: string; spaces_url: string; label: string }> {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('kind', kind)
        if (label) formData.append('label', label)
        if (profileContext?.profileType) formData.append('profileType', profileContext.profileType)
        if (profileContext?.profileName) formData.append('profileName', profileContext.profileName)
        const res = (await NodeAxios.post(`/campaigns/library/brand-assets/${brandAssetId}/profile-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })) as any
        return res?.data || res
    },

    /**
     * Attach a media library image as a profile image (image already in media center)
     */
    async attachProfileImageFromMedia(
        brandAssetId: string,
        mediaUrl: string,
        kind: string,
        profileContext?: { profileType?: string; profileName?: string; label?: string }
    ): Promise<{ id: string; kind: string; spaces_url: string; label: string }> {
        const res = (await NodeAxios.post(`/campaigns/library/brand-assets/${brandAssetId}/profile-images`, {
            media_url: mediaUrl,
            kind,
            label: profileContext?.label || kind,
            profileType: profileContext?.profileType,
            profileName: profileContext?.profileName,
        })) as any
        return res?.data || res
    },

    /**
     * Remove a profile image from a brand asset
     */
    async removeProfileImage(brandAssetId: string, imageId: string): Promise<{ success: boolean }> {
        return NodeAxios.delete(`/campaigns/library/brand-assets/${brandAssetId}/profile-images/${imageId}`) as any
    },
}

// ============================================
// Scraped Websites Cache API
// ============================================

export const scrapedWebsitesService = {
    /**
     * Get recent scrapes
     */
    async getRecentScrapes(params?: {
        type?: 'company' | 'product' | 'people'
        limit?: number
        skip?: number
        includeStale?: boolean
    }): Promise<{ success: boolean; scrapes: ScrapedWebsite[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.type) query.append('type', params.type)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))
        if (params?.includeStale) query.append('includeStale', String(params.includeStale))

        const suffix = query.toString() ? `?${query.toString()}` : ''
        return await NodeAxios.get(`/campaigns/scrapes${suffix}`)
    },

    /**
     * Get a scrape by ID
     */
    async getScrapeById(scrapeId: string): Promise<{ success: boolean; scrape: ScrapedWebsite }> {
        return await NodeAxios.get(`/campaigns/scrapes/${scrapeId}`)
    },

    /**
     * Save a scraped website
     */
    async saveScrape(data: {
        url: string
        content: any
        screenshot_url?: string
        type?: 'company' | 'product' | 'people'
        tags?: string[]
        scrape_duration?: number
        feature: 'targets' | 'goals' | 'alos' | 'visual-phd'
        campaign_id?: string
    }): Promise<ScrapedWebsite> {
        return await NodeAxios.post(`/campaigns/scrapes`, data)
    },

    /**
     * Track scrape usage
     */
    async trackUsage(scrapeId: string, feature: 'targets' | 'goals' | 'alos', campaignId?: string): Promise<void> {
        await NodeAxios.post(`/campaigns/scrapes/${scrapeId}/track`, {
            feature,
            campaign_id: campaignId,
        })
    },

    /**
     * Delete a scrape
     */
    async deleteScrape(scrapeId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/scrapes/${scrapeId}`)
    },

    /**
     * Get scraping stats
     */
    async getStats(): Promise<{
        total: number
        by_type: Record<string, number>
        recent_count: number
        most_used: ScrapedWebsite[]
    }> {
        return await NodeAxios.get(`/campaigns/scrapes/stats`)
    },
}

// ============================================
// Library ALO Groups API
// ============================================

export const libraryALOGroupsService = {
    /**
     * List library ALO groups
     */
    async listLibraryGroups(params?: {
        level?: 'company' | 'product' | 'people'
        sortBy?: 'usage_count' | 'created_at' | 'name'
        sortOrder?: 'asc' | 'desc'
        limit?: number
        skip?: number
    }): Promise<{ groups: LibraryALOGroup[]; total: number }> {
        const query = new URLSearchParams()
        if (params?.level) query.append('level', params.level)
        if (params?.sortBy) query.append('sortBy', params.sortBy)
        if (params?.sortOrder) query.append('sortOrder', params.sortOrder)
        if (params?.limit) query.append('limit', String(params.limit))
        if (params?.skip) query.append('skip', String(params.skip))

        const suffix = query.toString() ? `?${query.toString()}` : ''
        const result = await NodeAxios.get(`/campaigns/library/alo-groups${suffix}`)
        return result.data || result
    },

    /**
     * Get library ALO group by ID
     */
    async getLibraryGroup(groupId: string): Promise<LibraryALOGroup> {
        return await NodeAxios.get(`/campaigns/library/alo-groups/${groupId}`)
    },

    /**
     * Save ALO group to library
     */
    async saveToLibrary(data: {
        level: 'company' | 'product' | 'people'
        name: string
        description?: string
        tags?: string[]
        assignedToType?: 'product' | 'person'
        assignedToId?: string
        assignedToName?: string
        alos: Array<{
            type: 'asset' | 'liability' | 'omission'
            category: string
            text: string
            title?: string
            description?: string
            proof_points?: string[]
            proofUrl?: string
            proofStrength?: 'strong' | 'medium' | 'weak'
            persuasion_type?: string
            rhetoric_devices?: string[]
            related_products?: string[]
            confidence?: number
            isStrategic?: boolean
            priority?: number
        }>
    }): Promise<LibraryALOGroup> {
        return await NodeAxios.post(`/campaigns/library/alo-groups`, data)
    },

    /**
     * Delete library ALO group
     */
    async deleteLibraryGroup(groupId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/library/alo-groups/${groupId}`)
    },

    /**
     * Get library stats
     */
    async getStats(): Promise<{
        total: number
        by_level: Record<string, number>
        most_used: LibraryALOGroup[]
    }> {
        return await NodeAxios.get(`/campaigns/library/alo-groups/stats`)
    },

    /**
     * Load library group into campaign
     */
    async loadFromLibrary(campaignId: string, libraryGroupId: string): Promise<LibraryALOGroup> {
        return await NodeAxios.post(`/campaigns/${campaignId}/alos/groups/from-library`, { libraryGroupId })
    },
}

// ============================================
// Library TFD Service (Think-Feel-Do Outcomes)
// ============================================

export interface LibraryTFD {
    _id: string
    target_id: string
    org: string
    owner: string
    name: string
    description?: string
    campaign_type: 'product' | 'company' | 'people'
    tfd_simple?: {
        think: string
        feel: string
        do: string
    }
    tfd_structured?: {
        think?: any
        feel?: any
        do?: any
    }
    tfd_attention_levels?: Array<{
        level: 'MEGA' | 'MACRO' | 'MIDCRO' | 'MICRO' | 'NANO' | 'PICO'
        think: string
        feel: string
        do: string
    }>
    source_campaign_id?: string
    source_campaign_name?: string
    ai_generated?: boolean
    ai_model?: string
    generated_at?: string
    is_manually_edited?: boolean
    usage_count: number
    last_used_at?: string
    used_in_campaigns?: Array<{
        campaign_id: string
        added_at: string
    }>
    tags?: string[]
    category?: string
    is_private?: boolean
    is_deleted?: boolean
    createdAt: string
    updatedAt: string
}

export const libraryTFDService = {
    /**
     * List library TFDs
     */
    async listLibraryTFDs(params?: {
        targetId?: string
        campaignType?: 'product' | 'company' | 'people'
        sortBy?: 'usage' | 'recent' | 'name'
        limit?: number
        skip?: number
    }): Promise<{ tfds: LibraryTFD[]; total: number }> {
        return await NodeAxios.get(`/campaigns/library/tfds`, { params })
    },

    /**
     * Get TFDs for a specific target
     */
    async getTFDsForTarget(targetId: string): Promise<{ success: boolean; count: number; tfds: LibraryTFD[] }> {
        return await NodeAxios.get(`/campaigns/library/targets/${targetId}/tfds`)
    },

    /**
     * Get single library TFD
     */
    async getLibraryTFD(tfdId: string): Promise<LibraryTFD> {
        return await NodeAxios.get(`/campaigns/library/tfds/${tfdId}`)
    },

    /**
     * Generate TFD with AI
     */
    async generateLibraryTFD(params: {
        targetId: string
        campaignType: 'product' | 'company' | 'people'
        additionalContext?: string
    }): Promise<{
        success: boolean
        tfd: { think: string; feel: string; do: string; category?: string; tags?: string[] }
    }> {
        return await NodeAxios.post(`/campaigns/library/tfds/generate`, params)
    },

    /**
     * Create library TFD
     */
    async createLibraryTFD(data: {
        target_id: string
        name: string
        description?: string
        campaign_type: 'product' | 'company' | 'people'
        tfd_simple?: { think: string; feel: string; do: string }
        tfd_structured?: any
        tfd_attention_levels?: any[]
        tags?: string[]
        category?: string
        source_campaign_id?: string
        source_campaign_name?: string
        ai_generated?: boolean
        ai_model?: string
        generated_at?: string
    }): Promise<LibraryTFD> {
        return await NodeAxios.post(`/campaigns/library/tfds`, data)
    },

    /**
     * Update library TFD
     */
    async updateLibraryTFD(tfdId: string, data: Partial<LibraryTFD>): Promise<LibraryTFD> {
        return await NodeAxios.patch(`/campaigns/library/tfds/${tfdId}`, data)
    },

    /**
     * Delete library TFD
     */
    async deleteLibraryTFD(tfdId: string): Promise<{ success: boolean; message: string }> {
        return await NodeAxios.delete(`/campaigns/library/tfds/${tfdId}`)
    },

    /**
     * Bulk delete library TFDs
     */
    async bulkDeleteTFDs(tfdIds: string[]): Promise<{ success: boolean; deletedCount: number; message: string }> {
        return await NodeAxios.post(`/campaigns/library/tfds/bulk-delete`, { tfdIds })
    },

    /**
     * Duplicate library TFD
     */
    async duplicateLibraryTFD(tfdId: string, newName: string): Promise<LibraryTFD> {
        return await NodeAxios.post(`/campaigns/library/tfds/${tfdId}/duplicate`, { newName })
    },

    /**
     * Get library TFD statistics
     */
    async getStats(): Promise<{
        total: number
        by_campaign_type: Record<string, number>
        by_target: Array<{ targetId: string; count: number }>
        most_used: LibraryTFD[]
    }> {
        return await NodeAxios.get(`/campaigns/library/tfds/stats`)
    },

    /**
     * Search library TFDs
     */
    async searchLibraryTFDs(params: {
        q: string
        targetId?: string
        campaignType?: 'product' | 'company' | 'people'
        limit?: number
    }): Promise<{ success: boolean; count: number; tfds: LibraryTFD[] }> {
        return await NodeAxios.get(`/campaigns/library/tfds/search`, { params })
    },
}

// ============================================
// Story Generation Service
// ============================================

export interface GeneratedStory {
    headline: string
    paragraphs: string[]
}

export interface StoryGenerationResult {
    stories: {
        value: GeneratedStory
        problem: GeneratedStory
        aspiration: GeneratedStory
    }
    selected_angle?: string
    custom_headline?: string
    custom_paragraphs?: string[]
    metadata: {
        based_on: {
            goals_count: number
            targets_count: number
            alos_count: number
            assets_count: number
            liabilities_count: number
            omissions_count: number
        }
        generated_at: string
    }
    campaign_context?: {
        name: string
        description?: string | null
        campaign_type: 'product' | 'company' | 'people'
        companyName?: string | null
        productNames?: string[]
        peopleNames?: string[]
        targetNames?: string[]
        primaryTarget?: string | null
        primaryTargetDescription?: string | null
        goals?: Array<{
            name: string
            goal_type: 'product' | 'company' | 'people'
        }>
    }
}

export const storyService = {
    /**
     * Get cached campaign stories (if they exist)
     */
    async getStory(campaignId: string): Promise<StoryGenerationResult | null> {
        const result = await NodeAxios.get(`/campaigns/${campaignId}/story`)

        // Backend returns {success: true, data: {...}}
        // Axios response is: result.data = {success: true, data: {...}}
        // We want: result.data.data = {stories: {...}, metadata: {...}}
        const extracted = result.data?.data || result.data

        // If extracted still has 'success' field, we got the wrong level
        if (extracted && 'success' in extracted && extracted.data) {
            return extracted.data
        }

        return extracted // Will be null if no cached stories
    },

    /**
     * Generate campaign story variations from goals, targets, and ALOs
     */
    async generateStory(campaignId: string): Promise<StoryGenerationResult> {
        // Increase timeout for AI story generation - 3 parallel GPT-4o calls can take 60-90 seconds
        const result = await NodeAxios.post(
            `/campaigns/${campaignId}/generate-story`,
            {},
            {
                timeout: API_TIMEOUTS.AI_GENERATION,
            }
        )
        // Backend returns {success: true, data: {...}}, extract the data
        return result.data || result
    },

    /**
     * Save selected story angle and any custom edits
     */
    async saveStorySelection(
        campaignId: string,
        data: {
            selected_angle: 'value' | 'problem' | 'aspiration'
            custom_headline?: string
            custom_paragraphs?: string[]
        }
    ): Promise<void> {
        await NodeAxios.patch(`/campaigns/${campaignId}/story`, data)
    },
}

// ============================================
// Story Beat Service
// ============================================

export const storyBeatService = {
    /**
     * Generate beats from framework
     * POST /api/campaigns/:id/beats/generate
     */
    async generateBeats(campaignId: string, framework: string): Promise<{ beats: StoryBeat[]; count: number }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/generate`, { framework })
        const result = response.data || response
        return result
    },

    /**
     * List all beats for a campaign
     * GET /api/campaigns/:id/beats
     */
    async listBeats(campaignId: string): Promise<StoryBeat[]> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/beats`)
        const result = response.data || response
        return result || []
    },

    /**
     * Get a single beat
     * GET /api/campaigns/:id/beats/:beatId
     */
    async getBeat(campaignId: string, beatId: string): Promise<StoryBeat> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/beats/${beatId}`)
        return response.data || response
    },

    /**
     * Create a new beat
     * POST /api/campaigns/:id/beats
     */
    async createBeat(campaignId: string, beatData: Partial<StoryBeat>): Promise<StoryBeat> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats`, beatData)
        return response.data || response
    },

    /**
     * Update a beat
     * PATCH /api/campaigns/:id/beats/:beatId
     */
    async updateBeat(campaignId: string, beatId: string, updates: Partial<StoryBeat>): Promise<StoryBeat> {
        const response = await NodeAxios.patch(`/campaigns/${campaignId}/beats/${beatId}`, updates)
        return response.data || response
    },

    /**
     * Regenerate story_narrative and short_form_copy for a single beat using AI.
     * POST /api/campaigns/:campaignId/beats/:beatId/regenerate
     */
    async regenerateBeatNarrative(
        campaignId: string,
        beatId: string
    ): Promise<{ story_narrative: string; short_form_copy: string[] }> {
        const response = (await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/regenerate`, {})) as any
        return response
    },

    /**
     * Delete a beat
     * DELETE /api/campaigns/:id/beats/:beatId
     */
    async deleteBeat(campaignId: string, beatId: string): Promise<void> {
        await NodeAxios.delete(`/campaigns/${campaignId}/beats/${beatId}`)
    },

    /**
     * Add target message to beat
     * POST /api/campaigns/:id/beats/:beatId/target-messages
     */
    async addTargetMessage(
        campaignId: string,
        beatId: string,
        targetMessage: {
            target_id: string
            message: string
            key_points?: string[]
            alds_to_highlight?: string[]
        }
    ): Promise<StoryBeat> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/target-messages`, targetMessage)
        return response.data || response
    },

    /**
     * Update target message
     * PATCH /api/campaigns/:id/beats/:beatId/target-messages/:targetId
     */
    async updateTargetMessage(
        campaignId: string,
        beatId: string,
        targetId: string,
        messageData: {
            message?: string
            key_points?: string[]
            alds_to_highlight?: string[]
        }
    ): Promise<StoryBeat> {
        const response = await NodeAxios.patch(
            `/campaigns/${campaignId}/beats/${beatId}/target-messages/${targetId}`,
            messageData
        )
        return response.data || response
    },

    /**
     * Add channel strategy to beat
     * POST /api/campaigns/:id/beats/:beatId/channel-strategies
     */
    async addChannelStrategy(
        campaignId: string,
        beatId: string,
        channelStrategy: {
            channel: string
            strategy?: string
            asset_requirements?: string[]
            tone?: string
        }
    ): Promise<StoryBeat> {
        const response = await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/channel-strategies`,
            channelStrategy
        )
        return response.data || response
    },

    /**
     * Update channel strategy
     * PATCH /api/campaigns/:id/beats/:beatId/channel-strategies/:channel
     */
    async updateChannelStrategy(
        campaignId: string,
        beatId: string,
        channel: string,
        strategyData: {
            strategy?: string
            asset_requirements?: string[]
            tone?: string
            target_id?: string
            goal_ids?: string[]
            alo_ids?: string[]
            problem_ids?: string[]
            solution_ids?: string[]
            bait_model_id?: string
            context_document_ids?: string[]
            website_strategy?: any
        }
    ): Promise<StoryBeat> {
        const response = await NodeAxios.patch(
            `/campaigns/${campaignId}/beats/${beatId}/channel-strategies/${channel}`,
            strategyData
        )
        return response.data || response
    },

    /**
     * Remove channel strategy
     * DELETE /api/campaigns/:id/beats/:beatId/channel-strategies/:channel
     */
    async removeChannelStrategy(campaignId: string, beatId: string, channel: string): Promise<StoryBeat> {
        const response = await NodeAxios.delete(
            `/campaigns/${campaignId}/beats/${beatId}/channel-strategies/${channel}`
        )
        return response.data || response
    },

    /**
     * NEW: Set beat-level active strategy
     * POST /api/campaigns/:id/beats/:beatId/strategy
     */
    async setBeatStrategy(
        campaignId: string,
        beatId: string,
        strategyAssetId: string | null,
        strategyData: {
            target_id?: string
            target_name?: string
            goal_ids?: string[]
            alo_ids?: string[]
            problem_ids?: string[]
            solution_ids?: string[]
            strategy_text?: string
            tone?: string
            requirements?: string
        }
    ): Promise<StoryBeat> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/strategy`, {
            strategy_asset_id: strategyAssetId,
            ...strategyData,
        })
        return response.data || response
    },

    /**
     * NEW: Get active beat strategy
     * GET /api/campaigns/:id/beats/:beatId/strategy
     */
    async getBeatStrategy(campaignId: string, beatId: string): Promise<any> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/beats/${beatId}/strategy`)
        return response.data || response
    },

    /**
     * NEW: Remove beat strategy
     * DELETE /api/campaigns/:id/beats/:beatId/strategy
     */
    async clearBeatStrategy(campaignId: string, beatId: string): Promise<StoryBeat> {
        const response = await NodeAxios.delete(`/campaigns/${campaignId}/beats/${beatId}/strategy`)
        return response.data || response
    },

    /**
     * Mark beat as complete
     * PATCH /api/campaigns/:id/beats/:beatId/complete
     */
    async markComplete(campaignId: string, beatId: string, isComplete: boolean): Promise<StoryBeat> {
        const response = await NodeAxios.patch(`/campaigns/${campaignId}/beats/${beatId}/complete`, {
            is_complete: isComplete,
        })
        return response.data || response
    },

    /**
     * Generate topics for a beat using AI
     * POST /api/campaigns/:campaignId/beats/:beatId/topics/generate
     */
    async generateTopics(
        campaignId: string,
        beatId: string,
        data: {
            beatName: string
            beatDescription?: string
            channel: string
            templateId?: string // For website channel, specifies which template
            selectedStrategy?: {
                // For website channel, selected strategy for alignment
                headline: string
                angle: string
                description?: string
            }
            targetId?: string
            targetName?: string
            targetProfile?: {
                role?: string
                problems?: string[]
                solutions?: string[]
                aspirations?: string[]
                objections?: string[]
                values?: string[]
                influences?: string[]
            }
            goalIds?: string[]
            goalDescriptions?: string[]
            aloIds?: string[]
            aloTitles?: string[]
            baitModelId?: string
            baitModelName?: string
            contextDocuments?: { name: string; content?: string }[]
            selectedProblems?: string[]
            selectedSolutions?: string[]
            additionalContext?: string
            tone?: string
            count?: number
        }
    ): Promise<{ success: boolean; topics: any[]; count: number }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/topics/generate`, data)
        return response.data || response
    },

    /**
     * Generate strategic options for website templates (Level 1 of two-level system)
     * POST /api/campaigns/:campaignId/beats/:beatId/strategies/generate
     */
    async generateStrategies(
        campaignId: string,
        beatId: string,
        data: {
            beatNumber: number
            beatName: string
            beatDescription?: string
            channel: string
            templateId: string
            targetId?: string
            targetName?: string
            targetDescription?: string
            targetProfile?: {
                role?: string
                problems?: string[]
                solutions?: string[]
                aspirations?: string[]
                objections?: string[]
                values?: string[]
                influences?: string[]
            }
            targetProblems?: string[]
            targetAspirations?: string[]
            goalIds?: string[]
            goalDescriptions?: string[]
            goalType?: 'think' | 'feel' | 'do'
            goalDescription?: string
            aloIds?: string[]
            aloTitles?: string[]
            aloDescriptions?: string[]
            baitModelId?: string
            baitModelName?: string
            additionalContext?: string
        }
    ): Promise<{
        success: boolean
        strategies: Array<{
            id: string
            headline: string
            angle: string
            description: string
            bestFor: string
            keyElements: string[]
            why: string
            pros: string[]
            cons: string[]
        }>
        count: number
    }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/strategies/generate`, data, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        const result = response.data || response
        return result
    },

    /**
     * Generate content outlines for Universal Content Modal (template-specific)
     * POST /api/campaigns/:campaignId/beats/:beatId/outlines/generate
     */
    async generateOutlines(
        campaignId: string,
        beatId: string,
        data: {
            beatNumber: number
            beatName: string
            beatDescription?: string
            channel: string
            templateId?: string
            targetId?: string
            targetName?: string
            targetDescription?: string
            targetProfile?: {
                role?: string
                problems?: string[]
                solutions?: string[]
                pain_points?: string[]
                aspirations?: string[]
                objections?: string[]
                values?: string[]
                influences?: string[]
            }
            goalType?: 'think' | 'feel' | 'do'
            goalDescription?: string
            aloIds?: string[]
            aloTitles?: string[]
            aloDescriptions?: string[]
            alos?: Array<{
                id?: string
                type?: string
                title: string
                description?: string
                companyName?: string
                url?: string
                features?: string[]
                benefits?: string[]
                targetMarkets?: string[]
                competitiveAdvantages?: string[]
                pricingModel?: string
                integrations?: string[]
                [key: string]: any
            }> // NEW: Full ALO objects with company/product data
            baitModelName?: string
            angle?: string
            topicTitle?: string
            topicDescription?: string
            additionalContext?: string
            selectedStrategy?: {
                id: string
                headline: string
                angle: string
            }
            contextDocumentIds?: string[]
            selectedProblems?: string[]
            selectedSolutions?: string[]
            strategy?: string
            tone?: string
            count?: number
        }
    ): Promise<{
        success: boolean
        outlines: Array<{
            id: string
            headline: string
            outline: string
            angle: string
            alos: string[]
            why: string
        }>
        count: number
    }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/outlines/generate`, data, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        const result = response.data || response
        return result
    },

    /**
     * Regenerate 3 outline variations for a topic asset (Topic → 3 Outlines)
     */
    async regenerateOutlineVariations(
        campaignId: string,
        beatId: string,
        assetId: string,
        additionalInstructions?: string
    ): Promise<{
        success: boolean
        outlines: Array<{
            idea: string
            image_idea: string
            copy: string
        }>
        count: number
    }> {
        const response = await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/outlines/${assetId}/regenerate`,
            { additionalInstructions },
            { timeout: API_TIMEOUTS.AI_GENERATION }
        )
        return response.data || response
    },

    /**
     * Refine a specific field of an outline variation using AI
     */
    async refineOutlineField(
        campaignId: string,
        beatId: string,
        assetId: string,
        data: {
            variationIndex: number
            field: 'image_idea' | 'copy'
            instructions?: string
        }
    ): Promise<{ success: boolean; refinedText: string }> {
        const response = await NodeAxios.post(
            `/campaigns/${campaignId}/beats/${beatId}/outlines/${assetId}/refine-field`,
            data,
            { timeout: API_TIMEOUTS.AI_GENERATION }
        )
        return response.data || response
    },

    /**
     * Generate complete content for a channel using AI (SmartFill)
     * POST /api/campaigns/:campaignId/beats/:beatId/content/generate
     */
    async generateContent(
        campaignId: string,
        beatId: string,
        data: {
            beat: { beat_number: number; beat_name: string; description: string; purpose?: string }
            channelType: string
            contentType?: string
            topicTitle?: string
            topicDescription?: string
            target?: any
            goalType?: string
            goalDescription?: string
            alos?: any[]
            baitModel?: string
            contextDocuments?: any[]
            selectedProblems?: string[]
            selectedSolutions?: string[]
            library_story_context?: string
            library_story_ids?: string[]
            usePersona?: boolean
            strategy?: string
            tone?: string
            research_notes?: string
            dial_in?: {
                mundane_elements: string[]
                exciting_elements: string[]
                emotional_hooks: string[]
                key_tensions: string[]
            }
            selectedStrategy?: {
                id: string
                headline: string
                angle: string
            }
        }
    ): Promise<{ success: boolean; content: any }> {
        // Website site architecture requires longer timeout due to complex TFD-driven strategy generation
        const isWebsiteArchitecture = data.channelType === 'website' && data.contentType === 'site-architecture'
        const timeout = isWebsiteArchitecture ? 120000 : 30000 // 2 minutes for site architecture, 30s default

        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/content/generate`, data, {
            timeout,
        })
        return response.data || response
    },

    /**
     * Get beat statistics
     * GET /api/campaigns/:id/beats/stats
     */
    async getStats(campaignId: string): Promise<{
        total: number
        completed: number
        in_progress: number
        not_started: number
    }> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/beats/stats`)
        return response.data || response
    },

    /**
     * Archive beats (for framework changes)
     * POST /api/campaigns/:id/beats/archive
     */
    async archiveBeats(
        campaignId: string,
        beatIds: string[],
        reason: 'framework_change' | 'user_deleted' | 'manual' = 'framework_change'
    ): Promise<{ success: boolean; archived: number }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/archive`, { beatIds, reason })
        return response.data || response
    },

    /**
     * Restore archived beats
     * POST /api/campaigns/:id/beats/restore
     */
    async restoreBeats(campaignId: string, beatIds: string[]): Promise<{ success: boolean; restored: number }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/restore`, { beatIds })
        return response.data || response
    },

    /**
     * Generate dial-in elements using AI
     * POST /api/campaigns/:campaignId/beats/:beatId/generate-dial-in
     */
    async generateDialIn(
        campaignId: string,
        beatId: string
    ): Promise<{
        success: boolean
        dial_in: {
            mundane_elements: string[]
            exciting_elements: string[]
            emotional_hooks: string[]
            key_tensions: string[]
        }
    }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/generate-dial-in`)
        return response.data || response
    },

    /**
     * Generate strategic research notes using AI
     * POST /api/campaigns/:campaignId/beats/:beatId/generate-strategic-research
     */
    async generateStrategicResearch(
        campaignId: string,
        beatId: string,
        userContext?: string
    ): Promise<{
        success: boolean
        research_notes: string
    }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/${beatId}/generate-strategic-research`, {
            userContext,
        })
        return response.data || response
    },

    /**
     * Get archived beats
     * GET /api/campaigns/:id/beats/archived
     */
    async getArchivedBeats(campaignId: string): Promise<StoryBeat[]> {
        const response = await NodeAxios.get(`/campaigns/${campaignId}/beats/archived`)
        return response.data || response || []
    },

    /**
     * Check if beats have content
     * POST /api/campaigns/:id/beats/check-content
     */
    async checkBeatsContent(campaignId: string, beatIds: string[]): Promise<{ hasContent: boolean }> {
        const response = await NodeAxios.post(`/campaigns/${campaignId}/beats/check-content`, { beatIds })
        return response.data || response
    },
}

// ============================================
// Image Generation Service
// ============================================

export interface GenerateImageInput {
    prompt: string
    style?: string
    aspectRatio?: string
    useBrandColors?: boolean
    brandContext?: {
        colors?: string[]
        visualStyle?: string[]
    }
    // 🆕 Image preferences (from campaign settings)
    imagePreferences?: {
        style?: string
        colorPalette?: string
        mood?: string
        complexity?: string
        brandGuidelines?: string
        avoidElements?: string[]
        includeElements?: string[]
        sequenceContinuity?: string
        sequenceColorPalette?: string
    }
    campaignId?: string
    campaignName?: string
    beatNumber?: number
    channelType?: string
    postTitle?: string
}

export interface GeneratedImageResult {
    url: string
    revisedPrompt?: string
    savedToLibrary?: boolean
    documentId?: string
}

// ============================================
// Content Generation Service
// ============================================

export interface GenerateContentInput {
    assetId: string
    campaignId: string
    beatId?: string
    beatNumber?: number
    channelType: string
    topicTitle?: string
    topicDescription?: string
    topicId?: string
    // Strategic Context
    targetId?: string
    targetName?: string
    goalType?: string
    goalIds?: string[]
    goalDescription?: string
    aldIds?: string[]
    aldTitles?: string[]
    identityState?: 'failure' | 'transition' | 'success'
    visualPrompt?: string
    visualRunId?: string
    includeVisuals?: boolean
}

export interface GeneratedContentResult {
    success: boolean
    jobId: string
    message: string
    estimatedTime?: string
}

export const contentGenerationService = {
    /**
     * Generate content for a single asset (Content Studio)
     * POST /api/campaigns/assets/:assetId/generate-content
     */
    async generateContent(input: GenerateContentInput): Promise<GeneratedContentResult> {
        const { assetId, ...restData } = input

        const response = await NodeAxios.post(`/campaigns/assets/${assetId}/generate-content`, restData, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        const result = response.data || response

        return result
    },
}

// ============================================
// Image Generation Service
// ============================================

export interface GenerateImageBatchInput {
    campaignId: string
    prompt: string
    aspectRatio?: string
    n?: number
    assetId?: string
}

export interface GenerateImageBatchResult {
    success: boolean
    batch_id: string
    expected_count: number
}

export interface ImageBatchStatusResult {
    success: boolean
    batch_id: string
    status: 'pending' | 'partial' | 'complete' | 'failed'
    expected_count: number
    completed_count: number
    errors_count: number
    images: Array<{
        url: string
        thumbnail_url?: string
        document_id?: string
        job_index: number
        revised_prompt?: string
    }>
}

export const imageGenerationService = {
    /**
     * Generate an AI image
     * POST /api/campaigns/images/generate
     */
    async generateImage(input: GenerateImageInput): Promise<GeneratedImageResult> {
        // DALL-E 3 can take 45-60 seconds to generate images
        const response = await NodeAxios.post('/campaigns/images/generate', input, {
            timeout: API_TIMEOUTS.AI_GENERATION,
        })
        const result = response.data || response

        return result
    },

    /**
     * Enqueue a batch of N image generation jobs
     * POST /api/campaigns/images/generate-batch
     */
    async generateImageBatch(input: GenerateImageBatchInput): Promise<GenerateImageBatchResult> {
        const response = await NodeAxios.post('/campaigns/images/generate-batch', input, { timeout: 15000 })
        const result = response.data || response
        return result
    },

    /**
     * Get batch status for polling
     * GET /api/campaigns/images/batch/:batchId
     */
    async getImageBatchStatus(batchId: string): Promise<ImageBatchStatusResult> {
        const response = await NodeAxios.get(`/campaigns/images/batch/${batchId}`)
        const result = response.data || response
        return result
    },
}

// ============================================
// Featured Claims Service
// ============================================

export interface PromoteClaimRequest {
    claim_id: string
    source_profile_type: 'company' | 'product' | 'person'
    source_profile_id: string
    priority?: number
}

export interface PromoteClaimsResponse {
    success: boolean
    featured_claims: any[]
    promoted_count: number
}

/**
 * Promote Claims to Campaign Featured Claims
 * POST /api/campaigns/:campaignId/featured-claims
 */
export async function promoteClaimsToCampaign(
    campaignId: string,
    claims: PromoteClaimRequest[]
): Promise<PromoteClaimsResponse> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/featured-claims`, { claims })
    const result = response.data || response

    return result
}

// ============================================
// Multi-Campaign Type System Generation
// ============================================

/**
 * Queue Paid Ads Generation (RabbitMQ)
 * POST /api/campaigns/:campaignId/queue-ads-generation
 */
export async function queueCampaignAdsGeneration(
    campaignId: string,
    communicationBriefId: string,
    focusAngle?: string
): Promise<{ success: boolean; message: string }> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/queue-ads-generation`, {
        communicationBriefId,
        focusAngle,
    })

    const result = response.data || response
    return result
}

/**
 * Queue Landing Page Generation (RabbitMQ)
 * POST /api/campaigns/:campaignId/queue-landing-generation
 */
export async function queueCampaignLandingGeneration(
    campaignId: string,
    communicationBriefId: string,
    focusAngle?: string
): Promise<{ success: boolean; job_id: string }> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/queue-landing-generation`, {
        communicationBriefId,
        focusAngle,
    })

    const result = response.data || response
    return result
}

/**
 * Queue Organic Social Generation (RabbitMQ)
 * POST /api/campaigns/:campaignId/queue-social-generation
 */
export async function queueCampaignSocialGeneration(
    campaignId: string,
    communicationBriefId: string,
    focusAngle?: string
): Promise<{ success: boolean; job_id: string }> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/queue-social-generation`, {
        communicationBriefId,
        focusAngle,
    })

    const result = response.data || response
    return result
}

/**
 * Queue Sales/Outreach Generation (RabbitMQ)
 * POST /api/campaigns/:campaignId/queue-sales-generation
 */
export async function queueCampaignSalesGeneration(
    campaignId: string,
    communicationBriefId: string,
    focusAngle?: string
): Promise<{ success: boolean; message: string }> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/queue-sales-generation`, {
        communicationBriefId,
        focusAngle,
    })

    const result = response.data || response
    return result
}

/**
 * Queue Influencer Brief Adaptation (RabbitMQ)
 * POST /api/campaigns/:campaignId/adapt-brief
 */
export async function queueBriefAdaptation(
    campaignId: string,
    focusAngle?: string
): Promise<{ success: boolean; message: string }> {
    const response = await NodeAxios.post(`/campaigns/${campaignId}/adapt-brief`, {
        focusAngle,
    })

    const result = response.data || response
    return result
}
